import MobileMenu, { MobileMenuCSS } from "./MobileMenu";
import { supabase } from "./supabaseClient";
import React, { useState, useRef, useEffect } from "react";
import FloorVoteSimulator, { SENATE_MEMBERS, HOUSE_MEMBERS, buildPrompt, fetchAllFVMemberVotes, formatFVVotingStats } from "./LegiSIM_FloorVote";

// ─── MICHIGAN BILL STYLE GUIDE ────────────────────────────────────────────────
// Extracted from real 2025 Michigan legislation for authentic drafting

const MICHIGAN_BILL_STYLE = `
MICHIGAN LEGISLATIVE DRAFTING STANDARDS (103rd Legislature, 2025-2026 Session)

AUTHENTIC BILL STRUCTURE — Based on actual Michigan legislation:

1. HEADER FORMAT:
   HOUSE BILL NO. [number] or SENATE BILL NO. [number]
   [Date], Introduced by Rep./Sen. [Name] and referred to Committee on [Committee].

2. TITLE LINE (for new acts):
   A bill to [purpose]; to [purpose]; to [purpose]; and to [purpose].

3. AMENDATORY TITLE (for amending existing law):
   A bill to amend [year] PA [number], entitled
   "[full title of existing act],"
   (MCL [section range]) by [amending/adding] section[s] [numbers].

4. ENACTING CLAUSE (always capitalized):
   the people of the state of michigan enact:
   
   OR for enrolled bills:
   THE PEOPLE OF THE STATE OF MICHIGAN ENACT:

5. SECTION FORMAT:
   Sec. [number]. ([subsection]) [Text of law.]
   
   Subsections use: (1), (2), (3)...
   Subdivisions use: (a), (b), (c)...
   Sub-subdivisions use: (i), (ii), (iii)...

6. DEFINITIONS SECTION (standard):
   Sec. [X]. As used in this act:
   (a) "[Term]" means [definition].
   (b) "[Term]" means [definition], including, but not limited to, [examples].

7. PENALTIES/ENFORCEMENT:
   "[Person] is guilty of a [misdemeanor/felony] punishable by [penalty]."
   "[Person] is liable for a civil fine of not more than $[amount]."
   "[Person] is responsible for a state civil infraction."

8. ENACTING SECTIONS (at end):
   Enacting section 1. This act takes effect [date/condition].
   Enacting section 2. [additional conditions]

9. MCL CITATION FORMAT:
   [chapter].[section] — e.g., MCL 289.6101, MCL 400.1, MCL 28.421
   PA = Public Act — e.g., 1956 PA 218, 2000 PA 92, 1931 PA 328

10. STANDARD LINGUISTIC PATTERNS:
    - "shall" = mandatory obligation
    - "may" = permissive
    - "must" = requirement (modern usage)
    - "including, but not limited to," = non-exhaustive list
    - "as that term is defined in section [X] of [act]" = cross-reference
    - "subject to [condition]" = conditional
    - "except as provided under subsection (X)" = exception
    - "not later than [timeframe] after [trigger]" = deadline
    - "promulgate rules" = agency rulemaking authority
    - "the department shall" = agency obligation
    - "this state" not "Michigan" in operative text
    - "an individual" not "a person" for natural persons when distinction matters

11. REAL EXAMPLES OF MICHIGAN BILL LANGUAGE:

EXAMPLE 1 — Simple Amendment (HB 5402, 2025):
"A bill to amend 2000 PA 92 (MCL 289.6101).
the people of the state of michigan enact:
Sec. 6101. [amended text in bold]"

EXAMPLE 2 — Adding Section (HB 4482, 2025):
"A bill to amend 1927 PA 372 (MCL 28.421 to 28.435) by adding section 5q.
the people of the state of michigan enact:
Sec. 5q. The department of attorney general shall [obligation] at least every 60 days to [purpose]."

EXAMPLE 3 — New Act (SB 599, 2025):
"A bill to authorize certain activities involving [subject]; to require the licensing of certain people; to provide for the collection of fees; to provide for the powers and duties of certain state governmental officers and entities; to create certain funds; to require the promulgation of rules; to prohibit certain acts and prescribe civil sanctions and penalties; and to repeal acts and parts of acts.
THE PEOPLE OF THE STATE OF MICHIGAN ENACT:
CHAPTER I
GENERAL PROVISIONS
Sec. 101. This act may be cited as the '[Short Title] act.'
Sec. 103. As used in this act: [definitions]"

EXAMPLE 4 — Tax/Property Bill (SB 292, 2025):
"THE PEOPLE OF THE STATE OF MICHIGAN ENACT:
Sec. 7yy. (1) Subject to subsections (2) and (3), for taxes levied after December 31, 2025, [provision].
(2) To establish initial eligibility for [benefit] under subsection (1), an individual must do all of the following:
(a) Own and occupy as a principal residence the property for which the exemption is claimed.
(b) Be [age] years of age or older.
(c) File a claim, along with supporting documentation, affirming the individual's eligibility."

EXAMPLE 5 — Liability/Civil Bill (SB 390, 2025):
"THE PEOPLE OF THE STATE OF MICHIGAN ENACT:
Sec. 2980. (1) A person is liable to the [party] for damages that result from [harm], including, but not limited to, the [specific harm], that is caused by the person or by an animal owned by the person.
(2) All of the following may be awarded as damages in an action brought under this section:
(a) Economic damages, including, but not limited to, all of the following:
(i) The value of [item] if [condition].
(ii) The value of any [related item]."

FORMATTING NOTES:
- Line numbers appear in printed bills (1, 2, 3...) but are not part of the operative text
- Bill request numbers appear in header: e.g., (request no. H02428'25)
- Committee referral always noted in header introduction line
- "Final Page" notation appears at end of short bills
- Bold text = new language being added to existing law
- Strikethrough = language being removed
`;

const ANALYZER_SYSTEM = `You are WhipCount™ Bill Analyzer, a Michigan legislative intelligence tool. You have access to web search and should use it proactively.

IMPORTANT: If the user's message contains a section marked "⚠️ WHIPCOUNT DATABASE MATCH", that is the authoritative bill data from the 2025-2026 Michigan Legislature session stored in WhipCount's database. Use ONLY that data for your analysis — do NOT web search for a different version of the bill. The database has the correct current session bill.

If the message also contains "FULL BILL TEXT", use that text as the primary source for your analysis. Read it carefully before writing anything.

If no database match is provided and the user references a bill by number, you may search for it. Use: "Michigan [HB/SB] [number] 2025-2026 legislature site:legislature.mi.gov"

When analyzing a bill, provide a comprehensive structured analysis with these sections:

**BILL OVERVIEW**
- Bill number, type (new act, amendment, resolution, appropriation, etc.)
- Primary subject matter and policy area
- Prime sponsor, co-sponsors, and chamber
- Current status and committee assignment
- Applicable Michigan Compiled Laws (MCL) sections being created, amended, or repealed

**SUMMARY IN PLAIN ENGLISH**
- 3-5 sentence plain-language summary of what this bill actually does, written for a non-lawyer audience

**KEY PROVISIONS**
- Every operative section of the bill with what it specifically requires, prohibits, or permits
- New obligations created and who bears them
- Rights granted or removed
- Effective date or triggering conditions
- Any sunset clauses or expiration dates

**DEFINITIONS & SCOPE**
- All defined terms from the bill with their definitions
- Who/what entities are covered or explicitly excluded
- Geographic or temporal scope limitations

**ENFORCEMENT & PENALTIES**
- All civil and criminal penalties prescribed with specific amounts/ranges
- Which agency or official enforces the bill
- Private right of action if any
- Remedies available to affected parties

**FISCAL IMPACT**
- Specific dollar amounts appropriated (if appropriations bill)
- Estimated cost to state or local government
- Revenue impact — fees created, taxes changed
- Fund creation or transfers
- FTE impacts on state agencies

**POLITICAL LANDSCAPE**
- Likely caucus support/opposition based on subject matter and sponsor party
- Similar bills previously introduced in Michigan — passed, failed, or pending
- Key stakeholder groups who will support this bill and why
- Key stakeholder groups who will oppose this bill and why
- Lobbyist/industry interests at stake

**LEGISLATIVE PATHWAY**
- Current committee assignment and what that committee typically does with this type of bill
- What amendments or changes would be needed to advance it
- Companion bill in opposite chamber (if known)
- Likelihood of passage assessment

**POTENTIAL ISSUES & RISKS**
- Drafting ambiguities, gaps, or internal inconsistencies
- Constitutional concerns under Michigan or federal law
- Preemption issues with existing state or federal law
- Implementation or administrative challenges
- Unintended consequences

Be thorough and specific. Quote directly from the bill text when analyzing key provisions. If the text is an appropriations bill, list the specific line items. Flag anything unusual, controversial, or legally significant.`;


const CREATOR_SYSTEM = `You are WhipCount™ Bill Drafter, an expert Michigan legislative drafter trained on the authentic style, structure, and linguistics of real Michigan legislation from the 103rd Legislature (2025-2026 session).

${MICHIGAN_BILL_STYLE}

YOUR TASK:
When given a bill concept, idea, or outline, output a complete Michigan bill as plain text using authentic Michigan legislative formatting and language.

CRITICAL OUTPUT RULES:
- Output ONLY the bill text followed by DRAFTING NOTES. Nothing else.
- Do NOT say "I can't create files" or "I'm not able to export" — the platform handles file export automatically. Just output the bill text.
- Do NOT offer alternative methods or explain how to copy/paste. Just write the bill.
- Do NOT wrap the bill in markdown code blocks. Output it as plain text.
- Start your response immediately with "HOUSE BILL NO." or "SENATE BILL NO."

DRAFTING RULES:
1. Always start with the bill number placeholder: HOUSE BILL NO. ___ or SENATE BILL NO. ___
2. Include a realistic introduction line: "[Date], Introduced by Rep./Sen. [Name] and referred to Committee on [Committee]."
3. Use the correct title format — either amendatory ("A bill to amend...") or new act ("A bill to [enumerate purposes]...")
4. Use authentic Michigan enacting clause: "the people of the state of michigan enact:"
5. Number all sections as "Sec. X." with proper subsection hierarchy (1)(a)(i)
6. Use "shall" for mandatory, "may" for permissive
7. Use "this state" not "Michigan" in operative text
8. Include definitions section if the bill uses specialized terms
9. Include penalties/enforcement if the bill creates obligations
10. End with appropriate "Enacting section" for effective date or conditions
11. Reference real MCL sections where applicable (use plausible section numbers)
12. Match the linguistic register of real Michigan bills — formal, precise, no ambiguity

If the user provides a rough idea, flesh it out into a complete, properly formatted bill draft. If they provide detailed language, reformat it into authentic Michigan legislative style.

After the bill draft, provide a brief "DRAFTING NOTES" section explaining key choices made and any areas where additional legal review would be recommended.`;


const TESTIMONY_SYSTEM = `You are WhipCount™ Testimony Writer — an expert at crafting compelling, professional legislative testimony for the Michigan Legislature. You produce testimony that sounds authentic, persuasive, and tailored to the specific committee and legislative context.

YOUR TASK:
Given information about a bill, the user's organization, their position, the committee, and time constraints, produce three outputs:

## 1. WRITTEN TESTIMONY
A complete formal written testimony document (typically 1-2 pages) suitable for submission to the committee. Format:

**[ORGANIZATION NAME]**
**[POSITION] [BILL NUMBER/DESCRIPTION]**
**[COMMITTEE NAME]**
**[DATE PLACEHOLDER]**

[Body of testimony — 4-6 paragraphs covering:]
- Opening statement of position and who you represent
- Core argument supporting or opposing the bill
- Specific impacts on your organization/constituency
- Data, examples, or real-world context
- Response to likely counterarguments
- Closing call to action

**Respectfully submitted,**
**[NAME PLACEHOLDER]**
**[TITLE — leave blank if not provided], [ORGANIZATION]**

---

## 2. ORAL TESTIMONY SCRIPT
A spoken testimony script timed to the user's specified limit. Format for natural spoken delivery:
- Conversational but professional tone
- Clear pauses and emphasis points marked with [PAUSE] or **bold**
- Stays strictly within the time limit (estimate ~130 words per minute)
- NEVER invent or assume the speaker's title — if no title is provided, omit it entirely from the signature block
- Opens with "Good [morning/afternoon], Chair [NAME] and members of the committee..."
- Ends with offer to answer questions

---

## 3. TALKING POINTS SUMMARY
5-7 bullet points capturing the core arguments — ideal for leave-behind materials or quick reference during Q&A.

---

TONE AND STYLE:
- Authoritative but not aggressive
- Specific and factual — avoid vague platitudes
- Michigan-specific references where possible
- Match the formality level of real Michigan legislative testimony
- For SUPPORT testimony: emphasize benefits, build coalition appeal, address implementation
- For OPPOSITION testimony: identify specific harms, propose alternatives, focus on unintended consequences`;

const LEGISLATOR_PROFILES = `
MICHIGAN LEGISLATURE — 103rd SESSION — KEY PROFILES FOR PERSUASION STRATEGY:

SENATE LEADERSHIP:
- Winnie Brinks (D-29, Grand Rapids) — Majority Leader. Pragmatic coalition-builder. Responds to fiscal responsibility framing, healthcare equity, urban investment. Key to floor scheduling.
- Aric Nesbitt (R-20, Porter Township) — Minority Leader. Hard-line fiscal conservative. Responds to deregulation, tax relief, small business impact arguments. Will probe for fiscal notes.
- Jeremy Moss (D-7, Southfield) — President Pro Tem. Progressive champion on LGBTQ+ and civil rights. Responds to equity framing. Influential in steering D caucus.

HOUSE LEADERSHIP:
- Matt Hall (R-42, Allegan/Kalamazoo) — Speaker. Moderate Republican gatekeeper. Responds to bipartisan framing, economic development, job creation. Controls what reaches the floor.
- Bryan Posthumus (R-90, Kent) — Majority Leader. Conservative West Michigan. Responds to property rights, local control, fiscal responsibility.
- Ranjeev Puri (D-24, Wayne) — Minority Leader. Progressive Detroit-area voice. Responds to labor, equity, community investment framing.

KEY SWING VOTE PROFILES:
- Mallory McMorrow (D-8, Royal Oak/Troy) — Moderate suburban Democrat with national profile. Responds to pragmatic, cross-partisan framing. Influential with suburban Ds.
- Mark Huizenga (R-30, Walker/Wyoming) — Most moderate Senate Republican. Sometimes crosses aisle on business/fiscal issues. Key swing on close Senate votes.
- Michael Webber (R-9, Rochester Hills) — Moderate Oakland County Republican. Responds to small business, suburban quality-of-life arguments.
- Dayna Polehanki (D-5, Livonia/Westland) — Moderate suburban Democrat. Education, local government, women's rights focus. Key on close D-caucus votes.
- Karen Whitsett (D-4, Detroit) — Independent-minded Detroit Democrat. Has crossed aisle before. Responds to constituent impact and fiscal arguments.
- Joey Andrews (D-38, Allegan/Berrien) — Southwest MI Democrat in competitive district. Responds to economic and bipartisan framing. Persuadable by Republican-coded arguments.
- Reggie Miller (D-31, Lenawee/Monroe) — Moderate Democrat in swing district. Responds to working family, manufacturing, bipartisan themes.
- Denise Mentzer (D-61, Macomb) — Moderate Macomb Democrat. Labor, local government. Swing in tight D caucus votes.

CONSERVATIVE BLOC (needs economic/liberty framing):
- Rick Outman (R-33), Steve Carra (R-36), Matt Maddock (R-51), Josh Schriver (R-66), Gregory Markkanen (R-110) — Hard-line conservatives. Only persuadable via property rights, religious liberty, anti-mandate, or local control arguments. Fiscal savings also effective.

PROGRESSIVE BLOC (needs equity/justice framing):
- Erika Geiss (D-SD1), Stephanie Chang (D-SD3), Jeff Irwin (D-SD15), Noah Arbit (D-20), Helena Scott (D-8) — Progressive caucus. Needs equity, environmental justice, and civil rights framing. Will ask about disparate impact.

AGRICULTURAL BLOC (rural Republican):
- Dan Lauwers (R-SD25), Kevin Daley (R-SD26), Roger Victory (R-SD31), Ed McBroom (R-SD38), Nancy Jenkins-Arno (R-34) — Responds exclusively to agricultural impact, rural economy, property rights, water rights, and anti-federal-overreach framing.

LABOR BLOC (union Democrats):
- Paul Wojno (D-SD10), Darrin Camilleri (D-SD4), Tullio Liberati (D-2), Dylan Wegela (D-26) — Union-backed. Needs strong labor impact analysis, prevailing wage compatibility, and worker protection framing.
`;

const LOBBY_SYSTEM = `You are WhipCount™ Lobby Strategist — the most sophisticated legislative persuasion intelligence tool for the Michigan Legislature. You give government affairs professionals, lobbyists, and advocates the exact arguments they need to persuade each legislator to support their bill or policy idea.

${LEGISLATOR_PROFILES}

YOUR TASK:
The user will describe a bill concept or policy idea. You will produce a comprehensive legislator-by-legislator persuasion guide organized by argument type and legislator profile.

OUTPUT FORMAT — Structure your response exactly as follows:

## BILL CONCEPT SUMMARY
[2-3 sentence summary of what they're trying to pass and why]

## OVERALL STRATEGIC FRAMING
[The single most effective frame for this bill in the Michigan legislature — what story to tell, what values to invoke, what to avoid. 3-4 sentences.]

---

## IDEOLOGICAL ARGUMENTS
Arguments that align with legislators' core values and worldview.

### For Progressive Democrats (Districts 1-4, 7, 15, 20, etc.)
[2-3 specific arguments grounded in equity, justice, rights, environment, or community impact]

### For Moderate Democrats (swing suburban/rural Ds)
[2-3 arguments emphasizing pragmatism, constituent benefits, economic security, bipartisanship]

### For Moderate Republicans (Webber, Huizenga, Hall, Damoose, etc.)
[2-3 arguments emphasizing fiscal responsibility, economic opportunity, limited overreach, bipartisan credibility]

### For Conservative Republicans (Nesbitt, Outman, Maddock, Carra, etc.)
[2-3 arguments based on liberty, local control, property rights, anti-mandate, or deregulation]

---

## FINANCIAL & FISCAL ARGUMENTS
Arguments based on economic impact — critical for appropriations members and fiscal conservatives.

### Fiscal Cost/Savings Case
[Specific fiscal argument — cost savings to the state, economic activity generated, or fiscal note preparation advice]

### Economic Development Case
[Job creation, business attraction, industry impact, GDP contribution]

### Constituent Cost/Benefit
[How this helps or costs individuals and families in their districts]

---

## PARTY-STANCE ARGUMENTS
Arguments tailored to each party's current platform and caucus dynamics.

### For the Democratic Caucus
[How this aligns with current D caucus priorities — budget agenda, governor's priorities, national party platform connections]

### For the Republican Caucus
[How this aligns with current R caucus priorities — or how to neutralize R opposition through framing, amendments, or coalition]

### Bipartisan Angle
[Specific argument for why this should be a bipartisan bill — historical precedent, shared constituent interests, or issue areas where both parties have common ground]

---

## LEGISLATOR-SPECIFIC TALKING POINTS
Targeted arguments for key swing votes and power players. Use these in one-on-one meetings.

### Senate Leadership
**Winnie Brinks (D-29, Majority Leader):** [2 specific talking points]
**Aric Nesbitt (R-20, Minority Leader):** [2 specific talking points]

### House Leadership
**Matt Hall (R-42, Speaker):** [2 specific talking points]
**Ranjeev Puri (D-24, Minority Leader):** [2 specific talking points]

### Key Swing Senators
**Mallory McMorrow (D-8):** [1-2 talking points]
**Mark Huizenga (R-30):** [1-2 talking points]
**Michael Webber (R-9):** [1-2 talking points]
**Dayna Polehanki (D-5):** [1-2 talking points]

### Key Swing Representatives
**Karen Whitsett (D-4):** [1-2 talking points]
**Joey Andrews (D-38):** [1-2 talking points]
**Reggie Miller (D-31):** [1-2 talking points]

---

## ARGUMENTS TO AVOID
[3-4 specific framings, phrases, or arguments that will backfire with key vote groups — and why]

## ANTICIPATED OPPOSITION & COUNTER-ARGUMENTS
[What opponents will say, and how to preemptively neutralize each objection]

## LOBBY DAY SEQUENCING
[Recommended order of meetings — who to visit first, who to lock in early, who to approach last, and why]

Be highly specific. Use legislator names. Reference actual Michigan policy context, district demographics, and current political dynamics. This guide will be used in actual legislative meetings.`;

const AMENDMENT_SYSTEM = `You are WhipCount™ Amendment Builder — an expert Michigan legislative drafter who produces precisely formatted Michigan-style amendments.

WHEN GIVEN BILL TEXT AND AN AMENDMENT REQUEST:
1. Identify the exact section(s) that need to change
2. Draft the amendment using authentic Michigan legislative amendment language
3. Output the FULL amended bill text with ONLY the requested changes applied — every other section remains identical to the original

AMENDMENT FORMAT RULES:
- Use "Section X is amended to read as follows:" before each changed section
- Use "Section X is added to read as follows:" for new sections
- If repealing: "Section X is repealed."
- Preserve all MCL citations, subsection numbering, and statutory references exactly
- Maintain Michigan legislative style: "shall", "may", "is guilty of a misdemeanor punishable by..."
- Do NOT change punctuation, capitalization, or formatting in unchanged sections
- Do NOT add commentary or explanations inside the bill text
- Struck language (deletions) should be shown as ~~strikethrough~~ and new language in **bold** when showing redline view

OUTPUT STRUCTURE:
First output a short "AMENDMENT SUMMARY" (2-3 sentences describing what changed and why).
Then output the complete FULL AMENDED BILL TEXT starting from the bill header.
Finally output "CHANGES MADE:" with a bulleted list of exactly what was changed.

If the user's request is unclear or would conflict with existing statutory language, flag the issue before drafting.`;


// ─── STYLES ──────────────────────────────────────────────────────────────────


function displayStatus(s) {
  const MAP = {
    "Introduced":"Introduced","Engrossed":"In Progress","Enrolled":"PA Signed",
    "Chaptered":"PA Signed","Passed":"Passed","Failed":"Failed","Vetoed":"Vetoed",
    "Report Pass":"In Committee","Report Fail":"In Committee","Refer":"In Committee",
    "Override":"Passed","Draft":"Draft",
  };
  return MAP[s] || s || "Introduced";
}
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800&family=IBM+Plex+Sans:wght@300;400;500&family=IBM+Plex+Mono:wght@400;500&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
html,body{height:100%;}

/* Theme variables defined globally in public/theme.css */

body{background:var(--bg);color:var(--text);font-family:var(--font-body);-webkit-font-smoothing:antialiased;}

/* LAYOUT */
.page{display:flex;flex-direction:column;height:100vh;overflow:hidden;}

/* HEADER */
.header{padding:0.85rem 2rem;background:var(--surface);border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;flex-shrink:0;}
.header-left{display:flex;align-items:center;gap:1rem;}
.logo{font-family:var(--font-display);font-size:1.1rem;font-weight:800;color:var(--text);}
.logo em{font-style:normal;color:var(--gold);}
.logo sup{font-family:var(--font-body);font-size:0.5rem;color:var(--muted);vertical-align:super;}
.page-title{font-family:var(--font-mono);font-size:0.7rem;color:var(--muted);letter-spacing:0.1em;text-transform:uppercase;padding-left:1rem;border-left:1px solid var(--border);}

/* LEGISIM MASTHEAD */
.legisim-masthead{flex-shrink:0;background:var(--surface);border-bottom:2px solid rgba(201,168,76,0.22);padding:0 1.25rem;display:flex;align-items:center;justify-content:space-between;height:58px;position:relative;z-index:20;}
.legisim-masthead::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:linear-gradient(90deg,transparent 0%,var(--gold) 30%,#e8c96a 50%,var(--gold) 70%,transparent 100%);}
.legisim-masthead::after{content:'';position:absolute;inset:0;background-image:linear-gradient(rgba(201,168,76,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(201,168,76,0.025) 1px,transparent 1px);background-size:44px 44px;pointer-events:none;}
.masthead-left{display:flex;align-items:center;gap:1rem;z-index:1;}
.masthead-right{display:flex;align-items:center;gap:0.6rem;z-index:1;}
.mast-back{background:transparent;border:2px solid var(--gold-dim);border-radius:8px;padding:0.45rem 1.1rem;color:var(--gold);font-family:var(--font-mono);font-size:0.72rem;font-weight:600;cursor:pointer;letter-spacing:0.07em;transition:all 0.18s;white-space:nowrap;}
.mast-back:hover{border-color:var(--gold);background:var(--gold-glow);}
.mast-btn-sm{background:transparent;border:1px solid var(--border);border-radius:8px;padding:0.4rem 1rem;color:var(--muted);font-family:var(--font-mono);font-size:0.7rem;cursor:pointer;letter-spacing:0.06em;transition:all 0.18s;white-space:nowrap;}
.mast-btn-sm:hover{border-color:var(--gold-dim);color:var(--gold);}
.mast-btn-sm.signout:hover{border-color:rgba(192,57,43,0.5);color:var(--red);}
.masthead-wordmark{display:flex;align-items:baseline;font-family:var(--font-display);font-size:1.35rem;font-weight:800;letter-spacing:-0.02em;color:var(--text);line-height:1;white-space:nowrap;}
.masthead-wordmark em{font-style:italic;color:var(--gold);}
.masthead-wordmark sup{font-family:var(--font-mono);font-size:0.42rem;font-weight:500;color:var(--muted);vertical-align:super;letter-spacing:0.08em;margin-left:1px;}
.masthead-divider{width:1px;height:20px;background:var(--border);flex-shrink:0;}
.masthead-tagline{display:none;}
.masthead-badge{display:inline-flex;align-items:center;gap:0.3rem;background:rgba(201,168,76,0.08);border:1px solid rgba(201,168,76,0.2);border-radius:100px;padding:0.18rem 0.6rem;font-family:var(--font-mono);font-size:0.54rem;color:var(--gold);letter-spacing:0.1em;text-transform:uppercase;white-space:nowrap;}
.masthead-badge-dot{width:5px;height:5px;border-radius:50%;background:var(--gold);animation:mbpulse 2.5s infinite;}
@keyframes mbpulse{0%,100%{opacity:0.4;transform:scale(0.85)}50%{opacity:1;transform:scale(1.1)}}
.masthead-nav{display:flex;align-items:center;gap:0.3rem;z-index:1;flex-shrink:1;overflow:hidden;}
.mast-btn{background:transparent;border:1px solid var(--border);border-radius:6px;padding:0.36rem 0.7rem;color:var(--muted);font-family:var(--font-mono);font-size:0.64rem;font-weight:500;cursor:pointer;letter-spacing:0.07em;transition:all 0.18s;white-space:nowrap;display:flex;align-items:center;gap:0.3rem;}
.mast-btn:hover{border-color:var(--gold-dim);color:var(--gold);background:rgba(201,168,76,0.05);}
.mast-btn.gold{border-color:var(--gold-dim);color:var(--gold);}
.mast-btn.gold:hover{background:rgba(201,168,76,0.1);}
.mast-btn.active{border-color:var(--gold);color:var(--gold);background:rgba(201,168,76,0.1);}
.mast-sep{width:1px;height:16px;background:var(--border);margin:0 0.1rem;}

.home-btn{background:transparent;border:1px solid var(--border);border-radius:6px;padding:0.4rem 0.9rem;color:var(--muted);font-family:var(--font-mono);font-size:0.68rem;cursor:pointer;letter-spacing:0.06em;transition:all 0.2s;}
.home-btn:hover{border-color:var(--gold-dim);color:var(--gold);}

/* TOGGLE */
.toggle-bar{padding:1rem 2rem;border-bottom:1px solid var(--border);background:var(--surface);display:flex;align-items:center;justify-content:center;gap:0;flex-shrink:0;}
.toggle-grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:0.5rem;padding:1rem 1.5rem;border-bottom:1px solid var(--border);background:var(--surface);}
.toggle-btn{
  background:var(--bg);
  color:var(--muted);
  border:1px solid var(--border);
  border-radius:9px;
  padding:0.6rem 0.75rem;
  font-family:var(--font-mono);font-size:0.6rem;font-weight:600;
  cursor:pointer;letter-spacing:0.07em;text-transform:uppercase;
  transition:all 0.18s;text-align:center;
  line-height:1.4;
  position:relative;
}
.toggle-btn:hover{border-color:var(--gold-dim);color:var(--text);background:var(--surface2);}
.toggle-btn.active{color:var(--gold);border-color:var(--gold);background:rgba(201,168,76,0.07);}
.toggle-btn.active::after{content:'';position:absolute;top:6px;right:6px;width:5px;height:5px;border-radius:50%;background:var(--gold);}
.toggle-btn.active.analyzer,.toggle-btn.active.lobby,.toggle-btn.active.creator,.toggle-btn.active.testimony,.toggle-btn.active.amendment,.toggle-btn.active.floatvote{color:var(--gold);border-color:var(--gold);background:rgba(201,168,76,0.07);}
.toggle-desc{display:none;}
[data-theme="light"] .toggle-btn{background:#f8f9fc;border-color:rgba(26,39,68,0.12);color:#6b7280;}
[data-theme="light"] .toggle-btn:hover{border-color:rgba(160,120,48,0.3);color:#1a1e2e;background:#eef1f7;}
[data-theme="light"] .toggle-btn.active{color:#a07830;border-color:#a07830;background:rgba(160,120,48,0.06);}
[data-theme="light"] .toggle-btn.active::after{background:#a07830;}
[data-theme="light"] .toggle-btn.active.analyzer,[data-theme="light"] .toggle-btn.active.lobby,[data-theme="light"] .toggle-btn.active.creator,[data-theme="light"] .toggle-btn.active.testimony,[data-theme="light"] .toggle-btn.active.amendment,[data-theme="light"] .toggle-btn.active.floatvote{color:#a07830;border-color:#a07830;background:rgba(160,120,48,0.06);}

/* TESTIMONY FORM */
.testimony-form{padding:1rem 1.5rem;display:flex;flex-direction:column;gap:0.75rem;overflow-y:auto;flex:1;}
.form-field{display:flex;flex-direction:column;gap:0.3rem;}
.form-label{font-family:var(--font-mono);font-size:0.6rem;color:var(--gold);letter-spacing:0.1em;text-transform:uppercase;}
.form-input{background:var(--surface2);border:1px solid var(--border);border-radius:8px;padding:0.55rem 0.8rem;color:var(--text);font-family:var(--font-body);font-size:0.82rem;outline:none;transition:border-color 0.2s;width:100%;}
.form-input:focus{border-color:var(--gold-dim);}
.form-input::placeholder{color:var(--muted);}
.form-select{background:var(--surface2);border:1px solid var(--border);border-radius:8px;padding:0.55rem 0.8rem;color:var(--text);font-family:var(--font-body);font-size:0.82rem;outline:none;width:100%;cursor:pointer;}
.position-toggle{display:flex;gap:0;}
.pos-btn{flex:1;padding:0.5rem;font-family:var(--font-mono);font-size:0.68rem;letter-spacing:0.06em;border:1px solid var(--border);background:var(--surface2);color:var(--muted);cursor:pointer;transition:all 0.15s;}
.pos-btn:first-child{border-radius:6px 0 0 6px;border-right:none;}
.pos-btn:last-child{border-radius:0 6px 6px 0;}
.pos-btn.active.support{background:rgba(46,160,67,0.15);border-color:rgba(46,160,67,0.4);color:#4ade80;}
.pos-btn.active.oppose{background:rgba(239,68,68,0.15);border-color:rgba(239,68,68,0.4);color:#f87171;}

/* MAIN BODY */
.body{flex:1;display:flex;overflow:hidden;}

/* LEFT PANEL — INPUT */
.input-panel{width:42%;flex-shrink:0;border-right:1px solid var(--border);display:flex;flex-direction:column;overflow:hidden;background:var(--surface);}
.panel-header{padding:1rem 1.5rem;border-bottom:1px solid var(--border);flex-shrink:0;}
.panel-title{font-family:var(--font-display);font-size:1rem;font-weight:800;color:var(--text);letter-spacing:-0.01em;margin-bottom:0.35rem;}
.panel-sub{font-family:var(--font-body);font-size:0.8rem;color:var(--muted);font-weight:400;line-height:1.6;}

/* FILE UPLOAD */
.upload-zone{
  margin:1rem 1.5rem 0;
  border:1.5px dashed var(--border);border-radius:10px;
  padding:1rem;text-align:center;cursor:pointer;
  transition:border-color 0.2s,background 0.2s;
  flex-shrink:0;
}
.upload-zone:hover,.upload-zone.drag{border-color:var(--gold-dim);background:var(--gold-glow);}
.upload-zone input{display:none;}
.upload-icon{font-size:1.25rem;margin-bottom:0.3rem;display:block;}
.upload-text{font-size:0.75rem;color:var(--muted);font-family:var(--font-mono);}
.upload-text strong{color:var(--gold);}
.uploaded-file{
  margin:0.5rem 1.5rem 0;
  background:var(--charcoal);border:1px solid var(--border);
  border-radius:6px;padding:0.5rem 0.75rem;
  display:flex;align-items:center;justify-content:space-between;
  font-size:0.75rem;color:var(--text);font-family:var(--font-mono);
  flex-shrink:0;
}
.uploaded-file button{background:none;border:none;color:var(--muted);cursor:pointer;font-size:0.9rem;}
.uploaded-file button:hover{color:var(--rep);}

/* PDF WARNING */
.pdf-warning{margin:0.5rem 1.5rem 0;padding:0.55rem 0.85rem;background:rgba(239,68,68,0.08);border:1px solid rgba(239,68,68,0.35);border-radius:6px;font-family:var(--font-mono);font-size:0.68rem;color:#f87171;line-height:1.55;}
.bill-textarea{
  flex:1;margin:0.75rem 1.5rem;
  background:var(--surface2);border:1px solid var(--border);border-radius:8px;
  padding:1rem;color:var(--text);font-family:var(--font-mono);
  font-size:0.82rem;line-height:1.65;outline:none;resize:none;
  transition:border-color 0.2s;
}
.bill-textarea:focus{border-color:var(--gold-dim);}
.bill-textarea::placeholder{color:var(--muted);}

/* SUBMIT BUTTON */
.submit-row{padding:0.75rem 1.5rem 1rem;flex-shrink:0;display:flex;gap:0.75rem;align-items:center;}
.submit-btn{
  flex:1;padding:0.85rem;border:none;border-radius:8px;
  font-family:var(--font-body);font-size:0.9rem;font-weight:500;
  cursor:pointer;transition:all 0.2s;
}
.submit-btn.analyzer,.submit-btn.creator,.submit-btn.lobby,.submit-btn.testimony,.submit-btn.amendment,.submit-btn.floatvote{background:#c9a84c;color:#09090e;}
.submit-btn.analyzer:hover,.submit-btn.creator:hover,.submit-btn.lobby:hover,.submit-btn.testimony:hover,.submit-btn.amendment:hover,.submit-btn.floatvote:hover{background:#c9a84c;color:#09090e;transform:none;}
[data-theme="light"] .submit-btn.analyzer,[data-theme="light"] .submit-btn.creator,[data-theme="light"] .submit-btn.lobby,[data-theme="light"] .submit-btn.testimony,[data-theme="light"] .submit-btn.amendment,[data-theme="light"] .submit-btn.floatvote{background:#a07830;color:#fff;}
[data-theme="light"] .submit-btn.analyzer:hover,[data-theme="light"] .submit-btn.creator:hover,[data-theme="light"] .submit-btn.lobby:hover,[data-theme="light"] .submit-btn.testimony:hover,[data-theme="light"] .submit-btn.amendment:hover,[data-theme="light"] .submit-btn.floatvote:hover{background:#a07830;color:#fff;transform:none;}
.submit-btn:disabled{background:var(--surface2);color:var(--muted);cursor:not-allowed;transform:none;}
.clear-btn{background:transparent;border:1px solid var(--border);border-radius:8px;padding:0.85rem 1rem;color:var(--muted);font-family:var(--font-body);font-size:0.82rem;cursor:pointer;transition:all 0.2s;}
.clear-btn:hover{border-color:var(--border);color:var(--text);}

/* RIGHT PANEL — OUTPUT */
.output-panel{flex:1;display:flex;flex-direction:column;overflow:hidden;position:relative;}
.output-header{padding:1rem 1.5rem;border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;flex-shrink:0;}
.output-label{font-family:var(--font-mono);font-size:0.7rem;color:var(--gold);letter-spacing:0.12em;text-transform:uppercase;}
.copy-btn{background:transparent;border:1px solid var(--border);border-radius:6px;padding:0.35rem 0.75rem;color:var(--muted);font-family:var(--font-mono);font-size:0.65rem;cursor:pointer;letter-spacing:0.06em;transition:all 0.2s;}
.copy-btn:hover{border-color:var(--gold-dim);color:var(--gold);}
.copy-btn.copied{border-color:var(--green);color:var(--green);}

.output-body{flex:1;overflow-y:auto;padding:1.5rem;scrollbar-width:thin;scrollbar-color:var(--border) transparent;}
::-webkit-scrollbar{width:4px;}
::-webkit-scrollbar-thumb{background:var(--border);border-radius:4px;}

/* EMPTY STATE */
.empty-output{height:100%;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:0.75rem;color:var(--muted);text-align:center;padding:2rem;}
.empty-glyph{font-size:2rem;opacity:0.2;}
.empty-title{font-family:var(--font-display);font-size:1.2rem;color:var(--text);opacity:0.4;font-weight:700;}
.empty-sub{font-size:0.82rem;line-height:1.65;max-width:320px;font-weight:300;}

/* OUTPUT CONTENT */
.output-content{font-size:0.88rem;line-height:1.75;color:var(--text);}
.output-content h2{font-family:var(--font-mono);font-size:0.7rem;color:var(--gold);letter-spacing:0.12em;text-transform:uppercase;margin:1.5rem 0 0.6rem;padding-bottom:0.3rem;border-bottom:1px solid var(--border);}
.output-content h2:first-child{margin-top:0;}
.output-content p{margin-bottom:0.6rem;color:var(--text);}
.output-content ul{margin:0.4rem 0 0.8rem 1.2rem;}
.output-content li{margin-bottom:0.3rem;color:var(--muted);}
.output-content strong{color:var(--gold);}
.output-content code,.bill-draft{
  display:block;
  background:var(--surface);border:1px solid var(--border);border-radius:8px;
  padding:1.25rem 1.5rem;margin:0.75rem 0;
  font-family:var(--font-mono);font-size:0.8rem;line-height:1.8;
  color:var(--text);white-space:pre-wrap;word-break:break-word;
}

/* TYPING DOTS */
.typing{display:flex;gap:5px;align-items:center;padding:1rem 0;}
.typing-dot{width:7px;height:7px;border-radius:50%;background:var(--muted);animation:bounce 1.2s infinite;}
.typing-dot:nth-child(2){animation-delay:0.2s;}
.typing-dot:nth-child(3){animation-delay:0.4s;}
@keyframes bounce{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}

/* CONVERSATION HISTORY */
.history-turn{margin-bottom:1.75rem;padding-bottom:1.75rem;border-bottom:1px solid var(--border);}
.history-turn:last-child{border-bottom:none;}
.history-query{font-family:var(--font-mono);font-size:0.65rem;color:var(--muted);margin-bottom:0.5rem;letter-spacing:0.06em;}
.history-query span{color:var(--gold);}
.chat-bar{flex-shrink:0;display:flex;align-items:flex-end;gap:0.5rem;padding:0.75rem 1rem;border-top:1px solid var(--border);background:var(--surface);}
.chat-input{flex:1;background:var(--surface2);border:1px solid var(--border);border-radius:8px;padding:0.6rem 0.85rem;color:var(--text);font-family:var(--font-body);font-size:0.82rem;line-height:1.5;outline:none;resize:none;min-height:38px;max-height:120px;transition:border-color 0.2s;}
.chat-input:focus{border-color:var(--gold-dim);}
.chat-input::placeholder{color:var(--muted);}
.chat-send{flex-shrink:0;padding:0.55rem 1.1rem;background:var(--gold);color:#000;border:none;border-radius:8px;font-family:var(--font-body);font-size:0.82rem;font-weight:600;cursor:pointer;transition:all 0.15s;white-space:nowrap;}
.chat-send:hover{background:#e0b84a;}
.chat-send:disabled{background:var(--surface2);color:var(--muted);cursor:not-allowed;}
.whip-btn-wrap{position:relative;}
.whip-btn{background:transparent;border:1px solid var(--gold-dim);border-radius:6px;padding:0.35rem 0.75rem;color:var(--gold);font-family:var(--font-mono);font-size:0.65rem;cursor:pointer;letter-spacing:0.06em;transition:all 0.2s;white-space:nowrap;}
.whip-btn:hover{background:rgba(201,168,76,0.1);}
.whip-btn:disabled{opacity:0.4;cursor:not-allowed;}
.whip-menu{position:absolute;top:calc(100% + 4px);right:0;background:var(--charcoal);border:1px solid var(--border);border-radius:8px;box-shadow:0 8px 24px rgba(0,0,0,0.4);z-index:100;min-width:160px;overflow:hidden;}
.whip-menu-item{display:block;width:100%;padding:0.6rem 1rem;background:none;border:none;color:var(--text);font-family:var(--font-body);font-size:0.82rem;text-align:left;cursor:pointer;transition:background 0.15s;}
.whip-menu-item:hover{background:var(--surface2);color:var(--gold);}
@media(max-width:1200px){
  .mast-btn{padding:0.3rem 0.45rem;font-size:0.58rem;letter-spacing:0.03em;}
  .masthead-nav{gap:0.2rem;}
}
@media(max-width:900px){
  .mast-btn{padding:0.28rem 0.38rem;font-size:0.55rem;}
  .masthead-nav{gap:0.15rem;}
}
@media(max-width:768px){
  .masthead-nav{display:none !important;}
  .legisim-masthead{flex-shrink:0;background:var(--surface);border-bottom:2px solid rgba(201,168,76,0.22);padding:0 1.25rem;display:flex;align-items:center;justify-content:space-between;height:58px;position:relative;z-index:20;}
}


/* ── LIGHT MODE OVERRIDES ── */
[data-theme="light"] body{background:#eef1f7;color:#1a1e2e;}
[data-theme="light"] .page,[data-theme="light"] .app,[data-theme="light"] .bt-app,[data-theme="light"] .tl-app,[data-theme="light"] .map-app,[data-theme="light"] .cf-app{background:#eef1f7;}
[data-theme="light"] .legisim-masthead{background:#1a2744;border-bottom-color:rgba(255,255,255,0.1);}
[data-theme="light"] .legisim-masthead::before{background:linear-gradient(90deg,transparent,var(--gold),transparent);}
[data-theme="light"] .masthead-wordmark{color:#e6e4dc;}
[data-theme="light"] .masthead-wordmark em{color:#c9a84c;}
[data-theme="light"] .masthead-wordmark sup{color:rgba(230,228,220,0.5);}
[data-theme="light"] .mast-back{border-color:rgba(201,168,76,0.6);color:#c9a84c;background:transparent;}
[data-theme="light"] .mast-back:hover{background:rgba(201,168,76,0.12);border-color:#c9a84c;color:#c9a84c;}
[data-theme="light"] .mast-btn-sm{color:#e6e4dc;border-color:rgba(201,168,76,0.4);}
[data-theme="light"] .mast-btn-sm:hover{color:#ffffff;border-color:#c9a84c;background:rgba(201,168,76,0.1);}
[data-theme="light"] .mast-btn-sm.signout:hover{border-color:rgba(192,57,43,0.7);color:#ff6b6b;background:rgba(192,57,43,0.1);}
[data-theme="light"] .sidebar,[data-theme="light"] .bt-left,[data-theme="light"] .cal-sidebar,[data-theme="light"] .cf-sidebar,[data-theme="light"] .map-left{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .main,[data-theme="light"] .bt-detail,[data-theme="light"] .cf-main{background:#eef1f7;}
[data-theme="light"] .filter-btn,[data-theme="light"] .vh-filter-btn{background:#f8f9fc;border-color:rgba(26,39,68,0.12);color:#6b7280;}
[data-theme="light"] .filter-btn.active,[data-theme="light"] .vh-filter-btn.active{background:rgba(26,39,68,0.1);border-color:rgba(26,39,68,0.3);color:#1a2744;}
[data-theme="light"] .filter-btn.dem-active{background:var(--dem-bg);border-color:var(--dem);color:var(--dem);}
[data-theme="light"] .filter-btn.rep-active{background:var(--rep-bg);border-color:var(--rep);color:var(--rep);}
[data-theme="light"] .search-input,[data-theme="light"] .chat-input,[data-theme="light"] .bt-search-input,[data-theme="light"] .tl-search-input{background:#f8f9fc;border-color:rgba(26,39,68,0.12);color:#1a1e2e;}
[data-theme="light"] .leg-item.selected{background:#e8ecf4;}
[data-theme="light"] .leg-item.selected::before{background:#1a2744;}
[data-theme="light"] .leg-item:hover{background:#e8ecf4;}
[data-theme="light"] .chat-input-area{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .msg-bubble{background:#f0f2f8;border-color:rgba(26,39,68,0.1);}
[data-theme="light"] .msg.user .msg-bubble{background:#e8ecf4;border-color:rgba(26,39,68,0.2);}
[data-theme="light"] .send-btn{background:#1a2744;color:#f8f9fc;}
[data-theme="light"] .main-topbar,[data-theme="light"] .news-topbar,[data-theme="light"] .chamber-bar,[data-theme="light"] .toggle-bar{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .vh-header,[data-theme="light"] .vh-filters,[data-theme="light"] .vh-stats{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .vh-bill-number,[data-theme="light"] .bt-bill-number,[data-theme="light"] .bt-detail-number,[data-theme="light"] .tl-bill-number,[data-theme="light"] .district-num{color:#1a2744;}
[data-theme="light"] .profile-section-label,[data-theme="light"] .section-label,[data-theme="light"] .form-label,[data-theme="light"] .panel-title,[data-theme="light"] .output-label,[data-theme="light"] .floor-title,[data-theme="light"] .detail-title,[data-theme="light"] .search-section-label{color:#1a2744;}
[data-theme="light"] .input-panel{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .output-panel,[data-theme="light"] .output-body{background:#eef1f7;}
[data-theme="light"] .tl-add-btn,[data-theme="light"] .tl-refresh-btn,[data-theme="light"] .tl-view-btn{color:#1a2744;border-color:rgba(26,39,68,0.2);}
[data-theme="light"] .tl-table-head{background:#e8ecf4;}
[data-theme="light"] .tl-tr:hover{background:#e8ecf4;}
[data-theme="light"] .date-group-header{color:#1a2744;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .detail-header,[data-theme="light"] .profile-header,[data-theme="light"] .donors-section,[data-theme="light"] .bt-detail-header{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .acct-card{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .news-tab.active{color:#1a2744;border-color:#1a2744;}
[data-theme="light"] .story-source{color:#6b7280;}
[data-theme="light"] .profile-sidebar{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .home-title{color:#1a2744;}
[data-theme="light"] .cf-amount,[data-theme="light"] .cf-total{color:#1a2744;}
[data-theme="light"] .chamber-btn.active{background:rgba(26,39,68,0.1);color:#1a2744;border-color:rgba(26,39,68,0.3);}
[data-theme="light"] .swing-chip{background:rgba(26,39,68,0.1);color:#1a2744;}
[data-theme="light"] .score-cell.pass{background:rgba(37,107,66,0.12);}
[data-theme="light"] .score-cell.fail{background:rgba(176,48,32,0.08);}
[data-theme="light"] .map-tooltip-district{color:#1a2744;}
[data-theme="light"] .watchlist-btn-small,[data-theme="light"] .watch-btn{border-color:rgba(160,120,48,0.4);color:#a07830;}
[data-theme="light"] .vote-history-btn{background:rgba(160,120,48,0.08);border-color:rgba(160,120,48,0.35);color:#a07830;}
[data-theme="light"] .masthead-badge{background:rgba(26,39,68,0.08);border-color:rgba(26,39,68,0.2);}
[data-theme="light"] .masthead-badge-dot{background:#1a2744;}
[data-theme="light"] .mast-btn.gold{border-color:rgba(160,120,48,0.4);color:#a07830;}
[data-theme="light"] .mast-btn.active{background:rgba(26,39,68,0.1);border-color:rgba(26,39,68,0.3);color:#1a2744;}
[data-theme="light"] .issue-tag{background:#e8ecf4;border-color:rgba(26,39,68,0.12);color:#1a1e2e;}
[data-theme="light"] .qp-btn{background:#f0f2f8;border-color:rgba(26,39,68,0.12);color:#6b7280;}
[data-theme="light"] .empty-card{background:#f0f2f8;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .donor-bar-fill{background:linear-gradient(90deg,#1a2744,#2d4a80);}
[data-theme="light"] .cf-leg-item.active,[data-theme="light"] .bt-bill-item.selected{border-left-color:#1a2744;}
[data-theme="light"] .subject-tag{background:#e8ecf4;border-color:rgba(26,39,68,0.12);color:#1a1e2e;}
[data-theme="light"] .plan-badge,[data-theme="light"] .admin{color:#1a2744;}
[data-theme="light"] .login-card{background:#f8f9fc;border-color:rgba(26,39,68,0.12);box-shadow:0 4px 24px rgba(26,39,68,0.1);}
`;

// ─── HELPERS ─────────────────────────────────────────────────────────────────

function renderOutput(text) {
  const lines = text.split('\n');
  const elements = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith('**') && line.endsWith('**')) {
      elements.push(<h2 key={i}>{line.replace(/\*\*/g, '')}</h2>);
    } else if (line.startsWith('- ') || line.startsWith('• ')) {
      const items = [];
      while (i < lines.length && (lines[i].startsWith('- ') || lines[i].startsWith('• '))) {
        items.push(<li key={i}>{renderInline(lines[i].replace(/^[-•] /, ''))}</li>);
        i++;
      }
      elements.push(<ul key={`ul-${i}`}>{items}</ul>);
      continue;
    } else if (line.trim()) {
      elements.push(<p key={i}>{renderInline(line)}</p>);
    }
    i++;
  }
  return elements;
}

function renderInline(text) {
  return text.split(/\*\*(.*?)\*\*/g).map((part, i) =>
    i % 2 === 1 ? <strong key={i}>{part}</strong> : part
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

async function handleSignOut(supabaseClient, onNavigate) {
  await supabaseClient.auth.signOut();
  onNavigate('home');
}

// ─── FLOOR VOTE RATE LIMITING ─────────────────────────────────────────────────
const FV_MONTHLY_LIMIT = 25;

async function getFVUsage(userId) {
  const month = new Date().toISOString().slice(0, 7);
  const { data } = await supabase.from("query_usage").select("count").eq("user_id", userId).eq("query_date", month + "-fv").single();
  return data?.count || 0;
}

async function incrementFVUsage(userId) {
  const month = new Date().toISOString().slice(0, 7);
  const dateKey = month + "-fv";
  const { data } = await supabase.from("query_usage").select("id, count").eq("user_id", userId).eq("query_date", dateKey).single();
  if (data) {
    await supabase.from("query_usage").update({ count: data.count + 1, updated_at: new Date().toISOString() }).eq("id", data.id);
    return data.count + 1;
  } else {
    await supabase.from("query_usage").insert({ user_id: userId, query_date: dateKey, count: 1 });
    return 1;
  }
}

export default function BillStudio({ onNavigate, session, profile }) {
  const authHeader = session?.access_token ? { "Authorization": `Bearer ${session.access_token}` } : {};
  const [mode, setMode] = useState('analyzer'); // 'analyzer' | 'creator' | 'lobby' | 'testimony' | 'amendment' | 'floatvote'
  // Floor Vote state (managed here so it fits the standard input-panel)
  const [fvChamber, setFvChamber] = useState('Senate');
  const [fvBillText, setFvBillText] = useState('');
  const [fvFileName, setFvFileName] = useState(null);
  const [fvLoading, setFvLoading] = useState(false);
  const [fvUsed, setFvUsed] = useState(0);

  useEffect(() => {
    if (session?.user?.id && profile?.role !== "admin") {
      getFVUsage(session.user.id).then(count => setFvUsed(count));
    }
  }, [session]);
  const [fvResult, setFvResult] = useState(null);
  const [fvDrag, setFvDrag] = useState(false);
  const fvFileRef   = useRef(null);
  const amendFileRef = useRef(null);
  const [billText, setBillText] = useState('');
  const [fileName, setFileName] = useState(null);
  const [pdfWarning, setPdfWarning] = useState(null);
  const [fvPdfWarning, setFvPdfWarning] = useState(null);
  const [loading, setLoading] = useState(false);
  const [followUp, setFollowUp] = useState('');
  // Per-mode history — persists when switching between modes within the session
  const historyMap = {};
  const [historyByMode, setHistoryByMode] = useState({analyzer:[],creator:[],lobby:[],testimony:[],amendment:[],floatvote:[]});
  const history = historyByMode[mode] || [];
  const setHistory = (updater) => setHistoryByMode(prev => ({
    ...prev,
    [mode]: typeof updater === 'function' ? updater(prev[mode] || []) : updater
  }));
  const [whipSheetMenu, setWhipSheetMenu] = useState(false);
  const [whipSheetLoading, setWhipSheetLoading] = useState(false);
  // Amendment mode state
  const [amendBillNum,    setAmendBillNum]    = useState('');
  const [amendFileName,   setAmendFileName]   = useState(null);
  const [amendPdfWarning, setAmendPdfWarning] = useState(null);
  const [amendBillData, setAmendBillData] = useState(null);
  const [amendBillLoading, setAmendBillLoading] = useState(false);
  const [amendBillError, setAmendBillError] = useState(null);
  const [amendPrompt, setAmendPrompt] = useState('');
  const whipMenuRef = useRef(null);

  // Close whip sheet menu on outside click
  useEffect(() => {
    const handler = (e) => { if (whipMenuRef.current && !whipMenuRef.current.contains(e.target)) setWhipSheetMenu(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const followUpRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [drag, setDrag] = useState(false);
  const fileRef = useRef(null);
  const outputRef = useRef(null);
  const textareaRef = useRef(null);

  // Testimony form state
  const [lobbyBillNum, setLobbyBillNum] = useState('');
  const [lobbyBillData, setLobbyBillData] = useState(null);
  const [lobbyBillLoading, setLobbyBillLoading] = useState(false);
  const [lobbyBillError, setLobbyBillError] = useState(null);
  const [testimonyBillData,    setTestimonyBillData]    = useState(null);
  const [testimonyBillLoading, setTestimonyBillLoading] = useState(false);
  const [testimonyBillError,   setTestimonyBillError]   = useState(null);
  const [testimonyForm, setTestimonyForm] = useState({
    bill: '',
    organization: '',
    position: 'support',
    chamber: 'House',
    committee: '',
    timeLimit: '3',
    title: '',
    additionalContext: '',
  });

  // Reset input state when switching modes (history is preserved per-mode)
  useEffect(() => {
    setBillText('');
    setFileName(null);
    setPdfWarning(null);
    setAmendFileName(null);
    setAmendPdfWarning(null);
  }, [mode]);

  // Scroll output to TOP on new response
  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = 0;
    }
  }, [history]);

  const fetchBillData = async (rawNum) => {
    const raw = rawNum.trim().toUpperCase().replace(/\s+/g, '');
    let num = raw;
    const houseMatch = raw.match(/^(?:HOUSEBILL|HB)(\d+)$/);
    const senateMatch = raw.match(/^(?:SENATEBILL|SB)(\d+)$/);
    const numOnly = raw.match(/^(\d+)$/);
    if (houseMatch) num = 'HB' + houseMatch[1].padStart(4, '0');
    else if (senateMatch) num = 'SB' + senateMatch[1].padStart(4, '0');
    else if (numOnly) num = numOnly[1].length <= 3 ? 'SB' + numOnly[1].padStart(4, '0') : 'HB' + numOnly[1].padStart(4, '0');
    const searchRes = await fetch(`https://legisim-server.onrender.com/api/db/search?query=${encodeURIComponent(num)}`);
    const searchData = await searchRes.json();
    const results = searchData.results || searchData.bills || [];
    const match = results.find(b => b.bill_number === num) || results[0];
    if (!match) throw new Error(`No bill found for ${num}`);
    const detailRes = await fetch(`https://legisim-server.onrender.com/api/db/bill?id=${match.bill_id}`);
    const detailData = await detailRes.json();
    const bill = detailData.bill || match;
    const sponsors = (bill.sponsors || []).map(s => s.name + (s.sponsor_type_id === 1 ? ' (Primary)' : '') + ` (${s.party})`).join(', ');
    const hist = (bill.history || []).slice(0, 5).map(h => `${h.date}: ${h.action}`).join('\n');

    // Fetch full bill text for richer analysis
    let billText = null;
    let billTextType = null;
    let billTextDate = null;
    try {
      const textRes = await fetch(`https://legisim-server.onrender.com/api/legiscan/billtext?id=${match.bill_id}`);
      const textData = await textRes.json();
      if (textData.text) {
        billText = textData.text;
        billTextType = textData.text_type;
        billTextDate = textData.date;
      }
    } catch(e) { /* proceed without text */ }

    return { bill, sponsors, history: hist, num, billText, billTextType, billTextDate };
  };

  const handleLobbyBillLookup = async () => {
    if (!lobbyBillNum.trim()) return;
    setLobbyBillLoading(true);
    setLobbyBillError(null);
    try {
      const d = await fetchBillData(lobbyBillNum);
      setLobbyBillData(d);
    } catch(e) {
      setLobbyBillError(e.message || 'Lookup failed.');
      setLobbyBillData(null);
    }
    setLobbyBillLoading(false);
  };

  const handleTestimonyBillLookup = async () => {
    const v = testimonyForm.bill.trim();
    if (!v) return;
    setTestimonyBillLoading(true);
    setTestimonyBillError(null);
    try {
      const d = await fetchBillData(v);
      setTestimonyBillData(d);
    } catch(e) {
      setTestimonyBillError(e.message || 'Bill not found.');
      setTestimonyBillData(null);
    }
    setTestimonyBillLoading(false);
  };

  const handleAmendBillLookup = async () => {
    if (!amendBillNum.trim()) return;
    setAmendBillLoading(true);
    setAmendBillError(null);
    try {
      const d = await fetchBillData(amendBillNum);
      setAmendBillData(d);
      setHistory([]); // clear prior amendment history when bill changes
    } catch(e) {
      setAmendBillError(e.message || 'Bill not found.');
      setAmendBillData(null);
    }
    setAmendBillLoading(false);
  };

  const handleAmendSubmit = async () => {
    if (!amendBillData || !amendPrompt.trim() || loading) return;
    const prompt = amendPrompt.trim();
    setAmendPrompt('');
    setLoading(true);
    const fullMsg = `ORIGINAL BILL:\n\nBill: ${amendBillData.bill.bill_number}\nTitle: ${amendBillData.bill.title}\nSponsors: ${amendBillData.sponsors || 'N/A'}\nStatus: ${amendBillData.bill.status_text || 'Introduced'}\n${amendBillData.billText ? '\nFULL BILL TEXT:\n' + amendBillData.billText : ''}\n\nAMENDMENT REQUEST:\n${prompt}`;
    const apiMessages = [
      ...(history.length > 0 ? (history[history.length - 1].apiMessages || []) : []),
      { role: 'user', content: fullMsg },
    ];
    try {
      const response = await fetch('https://legisim-server.onrender.com/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 8000,
          system: AMENDMENT_SYSTEM,
          messages: apiMessages,
        }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      const reply = data.content?.map(c => c.type === 'text' ? c.text : '').filter(Boolean).join('') || 'Unable to process.';
      const newApiMessages = [...apiMessages, { role: 'assistant', content: reply }];
      if (history.length === 0) {
        setHistory([{ query: fullMsg, response: reply, apiMessages: newApiMessages }]);
      } else {
        setHistory(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { ...updated[updated.length - 1], response: reply, apiMessages: newApiMessages };
          return updated;
        });
      }
    } catch {
      setHistory(prev => prev.length === 0
        ? [{ query: fullMsg, response: 'Unable to process. Please check your connection.', apiMessages }]
        : prev.map((t, i) => i === prev.length - 1 ? { ...t, response: 'Unable to process.' } : t)
      );
    }
    setLoading(false);
  };

  // Floor Vote simulate
  // ─── PDF ENCRYPTION DETECTION ────────────────────────────────────────────────
  // Reads raw bytes to check for /Encrypt in the PDF cross-reference structure.
  // Returns true if the PDF appears to be password-protected or encrypted.
  const isPdfEncrypted = async (file) => {
    try {
      const buf = await file.arrayBuffer();
      const bytes = new Uint8Array(buf);
      // Convert first 64KB to string for header checks
      const head = new TextDecoder('latin1').decode(bytes.slice(0, 65536));
      if (head.includes('/Encrypt')) return true;
      // Also check last 2KB where xref trailer lives
      const tail = new TextDecoder('latin1').decode(bytes.slice(Math.max(0, bytes.length - 2048)));
      if (tail.includes('/Encrypt')) return true;
      return false;
    } catch {
      return false;
    }
  };

  // Checks whether extracted text looks like real content vs binary garbage
  const isReadableText = (text) => {
    if (!text || text.trim().length < 50) return false;
    const printable = text.replace(/[^\x20-\x7E\n\r\t]/g, '');
    return printable.length / text.length > 0.85;
  };

  const fvHandleFile = async (file) => {
    if (!file) return;
    if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
      const encrypted = await isPdfEncrypted(file);
      if (encrypted) {
        setFvFileName(file.name);
        setFvBillText('');
        setFvPdfWarning('⚠️ This PDF appears to be password-protected or encrypted. The AI cannot read its contents. Open the PDF, press Ctrl+A to select all text, then copy and paste it into the text box below.');
        return;
      }
      const text = await file.text();
      if (!isReadableText(text)) {
        setFvFileName(file.name);
        setFvBillText('');
        setFvPdfWarning('⚠️ This PDF could not be read — it may be scanned, image-based, or security-restricted. Open the PDF, press Ctrl+A to select all text, then copy and paste it into the text box below.');
        return;
      }
      setFvPdfWarning(null);
      setFvFileName(file.name);
      setFvBillText(text);
    } else {
      setFvPdfWarning(null);
      setFvFileName(file.name);
      setFvBillText(await file.text());
    }
  };

  const fvSimulate = async () => {
    if (profile?.role !== "admin" && fvUsed >= FV_MONTHLY_LIMIT) {
      setFvResult({ error: `You've reached your ${FV_MONTHLY_LIMIT} monthly simulation limit. Resets on the 1st of next month.` });
      return;
    }
    if (!fvBillText.trim() || fvLoading) return;
    setFvLoading(true);
    setFvResult(null);
    try {
      const fvMembers = fvChamber === "Senate" ? SENATE_MEMBERS : HOUSE_MEMBERS;
      let votingStats = "";
      try {
        const mv = await fetchAllFVMemberVotes(fvMembers);
        votingStats = formatFVVotingStats(mv, fvMembers);
      } catch(e) {}
      const maxTok = fvChamber === "House" ? 8000 : 5000;
      const response = await fetch("https://legisim-server.onrender.com/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json",
          ...authHeader },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: maxTok,
          system: buildPrompt(fvChamber, votingStats),
          messages: [{ role: "user", content: `Simulate a floor vote in the Michigan ${fvChamber} for:

${fvBillText.trim()}` }],
        }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (data.error) throw new Error(data.error);
      const text = data.content?.map(c => c.text || "").join("") || "";
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (!jsonMatch) throw new Error("No JSON in response");
      setFvResult(JSON.parse(jsonMatch[0]));
    } catch (e) {
      setFvResult({ error: `Simulation failed: ${e.message}` });
    }
    setFvLoading(false);
  };

  const handleAmendFile = async (file) => {
    if (!file) return;
    if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
      const encrypted = await isPdfEncrypted(file);
      if (encrypted) {
        setAmendFileName(file.name);
        setAmendPdfWarning('⚠️ This PDF appears to be password-protected or encrypted. The AI cannot read its contents. Open the PDF, press Ctrl+A to select all text, then copy and paste it into the description box below.');
        return;
      }
      const text = await file.text();
      if (!isReadableText(text)) {
        setAmendFileName(file.name);
        setAmendPdfWarning('⚠️ This PDF could not be read — it may be scanned or image-based. Open the PDF, press Ctrl+A to select all text, then copy and paste it into the description box below.');
        return;
      }
      setAmendPdfWarning(null);
      setAmendFileName(file.name);
      setAmendPrompt(text);
    } else {
      setAmendPdfWarning(null);
      setAmendFileName(file.name);
      const text = await file.text();
      setAmendPrompt(text);
    }
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
      const encrypted = await isPdfEncrypted(file);
      if (encrypted) {
        setFileName(file.name);
        setBillText('');
        setPdfWarning('⚠️ This PDF appears to be password-protected or encrypted. The AI cannot read its contents. Open the PDF, press Ctrl+A to select all text, then copy and paste it into the box below.');
        return;
      }
      const text = await file.text();
      if (!isReadableText(text)) {
        setFileName(file.name);
        setBillText('');
        setPdfWarning('⚠️ This PDF could not be read — it may be scanned, image-based, or security-restricted. Open the PDF, press Ctrl+A to select all text, then copy and paste it into the text box below.');
        return;
      }
      setPdfWarning(null);
      setFileName(file.name);
      setBillText(text);
    } else {
      setPdfWarning(null);
      setFileName(file.name);
      setBillText(await file.text());
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleSubmit = async () => {
    const isTestimony = mode === 'testimony';
    // Build lobby bill context with full text
    const lobbyBillTextSection = lobbyBillData?.billText
      ? `\n\nFULL BILL TEXT (${lobbyBillData.billTextType || 'Introduced'}, ${lobbyBillData.billTextDate || ''}):\n${lobbyBillData.billText}`
      : '';
    const billContext = lobbyBillData
      ? `\n\nBILL DATA FROM MICHIGAN LEGISLATURE DATABASE:\nBill: ${lobbyBillData.bill.bill_number}\nTitle: ${lobbyBillData.bill.title}\nDescription: ${lobbyBillData.bill.description || 'N/A'}\nSponsors: ${lobbyBillData.sponsors || 'N/A'}\nStatus: ${lobbyBillData.bill.status_text || 'Introduced'}\nLast Action: ${lobbyBillData.bill.last_action || 'N/A'}${lobbyBillTextSection}` : '';

    // Build testimony bill context with full text
    const testimonyBillTextSection = testimonyBillData?.billText
      ? `\n\nFULL BILL TEXT (${testimonyBillData.billTextType || 'Introduced'}, ${testimonyBillData.billTextDate || ''}):\n${testimonyBillData.billText}`
      : '';
    const testimonyBillContext = testimonyBillData
      ? `\n\nBILL DATA FROM MICHIGAN LEGISLATURE DATABASE:\nBill: ${testimonyBillData.bill.bill_number}\nTitle: ${testimonyBillData.bill.title}\nDescription: ${testimonyBillData.bill.description || 'N/A'}\nSponsors: ${testimonyBillData.sponsors || 'N/A'}\nStatus: ${testimonyBillData.bill.status_text || 'Introduced'}${testimonyBillTextSection}`
      : '';

    let query = isTestimony
      ? `Please write testimony for the following:\n\nBill/Issue: ${testimonyForm.bill}\nOrganization: ${testimonyForm.organization}\nTitle: ${testimonyForm.title || 'not provided — omit from signature'}\nPosition: ${testimonyForm.position.toUpperCase()}\nCommittee: ${testimonyForm.chamber} ${testimonyForm.committee}\nOral time limit: ${testimonyForm.timeLimit} minutes${testimonyBillContext}${testimonyForm.additionalContext ? '\n\nADDITIONAL CONTEXT FROM USER:\n' + testimonyForm.additionalContext : ''}`
      : mode === 'lobby' ? (billText.trim() || '') + billContext
      : billText.trim();

    if (!query || loading) return;
    setLoading(true);

    // ── Analyzer: pre-fetch bill from Supabase if user referenced a bill number ──
    // Handles: HB5614, HB 5614, HB-5614, HB:5614, 5614 (house=4 digits, senate=3),
    //          "House Bill 5614", "Senate Bill 599", "hb5614", "sb 99", etc.
    if (mode === 'analyzer') {
      const parseBillNumber = (text) => {
        const t = text.trim();

        // Pattern 1: explicit prefix — HB/SB/HJ/SJ with optional separator + digits
        // e.g. HB5614, HB 5614, HB-5614, HB:5614, hb 5614
        const explicit = t.match(/\b(H(?:ouse)?[_\s-]?B(?:ill)?|S(?:enate)?[_\s-]?B(?:ill)?|HJ|SJ)\s*[-:.\s]?\s*(\d{2,5})\b/i);
        if (explicit) {
          const raw = explicit[1].replace(/[^a-zA-Z]/g,'').toUpperCase();
          const num = explicit[2];
          let prefix;
          if (raw.startsWith('H'))      prefix = 'HB';
          else if (raw.startsWith('S')) prefix = 'SB';
          else prefix = raw.slice(0,2).toUpperCase();
          return { prefix, num };
        }

        // Pattern 2: pure digits only — 4 digits = House Bill, 3 digits = Senate Bill
        const digitsOnly = t.match(/\b(\d{3,4})\b/);
        if (digitsOnly) {
          const num = digitsOnly[1];
          const prefix = num.length === 4 ? 'HB' : 'SB';
          return { prefix, num };
        }

        return null;
      };

      const parsed = parseBillNumber(query);
      if (parsed) {
        try {
          const { prefix, num } = parsed;
          // LegiScan stores as "HB 5614" — build candidate formats
          const padded    = num.padStart(prefix === 'HB' ? 4 : 3, '0');
          const candidates = [
            prefix + ' ' + padded,       // "HB 5614" — LegiScan standard
            prefix + padded,              // "HB5614"
            prefix + ' ' + num,           // "HB 614" (no pad)
            prefix + num,                 // "HB614"
          ];

          // Single search using the LegiScan-standard format
          const searchRes = await fetch(
            `https://legisim-server.onrender.com/api/db/search?query=${encodeURIComponent(prefix + ' ' + padded)}`
          );
          const searchData = await searchRes.json();
          const results = searchData.results || searchData.bills || [];

          // Find best match — exact bill_number match preferred, fallback to first result
          const match = results.find(b => candidates.includes(b.bill_number)) || results[0];

          if (match) {
            const detailRes = await fetch(`https://legisim-server.onrender.com/api/db/bill?id=${match.bill_id}`);
            const detailData = await detailRes.json();
            const bill = detailData.bill || match;
            const sponsors = (bill.sponsors || []).map(s =>
              s.name + (s.sponsor_type_id === 1 ? ' (Primary)' : '') + ` (${s.party})`
            ).join(', ');
            const hist = (bill.history || []).slice(0, 5).map(h => `${h.date}: ${h.action}`).join('\n');

            // Fetch full bill text from LegiScan for deep analysis
            let billTextSection = '';
            try {
              const textRes = await fetch(`https://legisim-server.onrender.com/api/legiscan/billtext?id=${match.bill_id}`);
              const textData = await textRes.json();
              if (textData.text) {
                billTextSection = `\n\nFULL BILL TEXT (${textData.text_type || 'Introduced'}, ${textData.date || ''}):\n${textData.text}`;
              }
            } catch(e) {
              // Text fetch failed — proceed with metadata only
            }

            const dbContext = `\n\n⚠️ WHIPCOUNT DATABASE MATCH — USE THIS, DO NOT WEB SEARCH FOR A DIFFERENT VERSION:\nBill: ${bill.bill_number} (2025-2026 Michigan Legislature session)\nTitle: ${bill.title}\nDescription: ${bill.description || 'N/A'}\nSponsors: ${sponsors || 'N/A'}\nStatus: ${bill.status_text || 'Introduced'}\nLast Action: ${bill.last_action || 'N/A'}\nAction Date: ${bill.last_action_date || 'N/A'}\nRecent History:\n${hist}${billTextSection}\n\nAnalyze this bill based on the above data.`;
            query = query + dbContext;
          }
        } catch(e) {
          // DB lookup failed — fall through to normal web search
        }
      }
    }

    const apiMessages = [
      ...history.flatMap(h => [
        { role: 'user', content: h.query },
        { role: 'assistant', content: h.response },
      ]),
      { role: 'user', content: query },
    ];

    const systemPrompt = mode === 'analyzer' ? ANALYZER_SYSTEM
      : mode === 'lobby' ? LOBBY_SYSTEM
      : mode === 'testimony' ? TESTIMONY_SYSTEM
      : mode === 'amendment' ? AMENDMENT_SYSTEM
      : CREATOR_SYSTEM;

    try {
      const response = await fetch('https://legisim-server.onrender.com/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: mode === 'analyzer' ? 8000 : mode === 'testimony' ? 4000 : mode === 'lobby' ? 6000 : 4000,
          system: systemPrompt,
          messages: apiMessages,
          tools: mode === 'analyzer' ? [{ type: "web_search_20250305", name: "web_search" }] : undefined,
        }),
      });
      if (!response.ok) throw new Error(`Proxy error: ${response.status}`);
      const data = await response.json();
      const reply = data.content?.map(c => c.type === 'text' ? c.text : '').filter(Boolean).join('') || 'Analysis unavailable.';
      // Store apiMessages on the turn so follow-ups can extend the exact message chain
      setHistory(prev => [...prev, {
        query,
        response: reply,
        apiMessages: [...apiMessages, { role: 'assistant', content: reply }],
      }]);
      if (!isTestimony) { setBillText(''); setFileName(null); }
    } catch {
      setHistory(prev => [...prev, { query, response: 'Unable to process. Please check your connection and try again.' }]);
    }
    setLoading(false);
  };

  const generateWhipSheet = async (format) => {
    setWhipSheetMenu(false);
    setWhipSheetLoading(true);
    try {
      const lastResponse = history[history.length - 1]?.response || '';
      const billNum = lobbyBillData?.bill?.bill_number || '';
      const billTitle = lobbyBillData?.bill?.title || '';
      const res = await fetch('https://legisim-server.onrender.com/api/whipsheet', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lobbyStrategy: lastResponse,
          billNumber: billNum,
          billTitle: billTitle,
          format,
        }),
      });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ext = format === 'txt' ? 'txt' : format === 'docx' ? 'docx' : 'pdf';
      a.download = `whipsheet-${(billNum || 'bill').replace(/\s/g,'')}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch(e) {
      alert('WhipSheet generation failed: ' + e.message);
    }
    setWhipSheetLoading(false);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && e.ctrlKey) handleSubmit();
  };

  const handleFollowUp = async () => {
    if (!followUp.trim() || loading || history.length === 0) return;
    const msg = followUp.trim();
    setFollowUp('');
    setLoading(true);

    const systemPrompt = mode === 'analyzer' ? ANALYZER_SYSTEM
      : mode === 'lobby' ? LOBBY_SYSTEM
      : mode === 'testimony' ? TESTIMONY_SYSTEM
      : mode === 'amendment' ? AMENDMENT_SYSTEM
      : CREATOR_SYSTEM;

    // Build full message chain: original enriched message + response + all prior
    // revision requests/responses + this new request.
    // h.apiMessages stores the exact messages array used for that turn.
    const last = history[history.length - 1];
    const priorMessages = last.apiMessages || [
      { role: 'user', content: last.query },
      { role: 'assistant', content: last.response },
    ];
    const apiMessages = [...priorMessages, { role: 'user', content: msg }];

    try {
      const response = await fetch('https://legisim-server.onrender.com/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: mode === 'analyzer' ? 8000 : mode === 'testimony' ? 4000 : mode === 'lobby' ? 6000 : 4000,
          system: systemPrompt,
          messages: apiMessages,
          tools: mode === 'analyzer' ? [{ type: "web_search_20250305", name: "web_search" }] : undefined,
        }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      const reply = data.content?.map(c => c.type === 'text' ? c.text : '').filter(Boolean).join('') || 'Unable to process.';

      // Update the last turn: new response + extend apiMessages for next follow-up
      setHistory(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          ...updated[updated.length - 1],
          response: reply,
          // Store the full message chain including this exchange
          // so the next follow-up has complete context
          apiMessages: [
            ...apiMessages,
            { role: 'assistant', content: reply },
          ],
        };
        return updated;
      });
    } catch {
      setHistory(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          ...updated[updated.length - 1],
          response: 'Unable to process. Please check your connection and try again.',
        };
        return updated;
      });
    }
    setLoading(false);
  };

  const copyAll = () => {
    const allText = history.map(h => h.response).join('\n\n---\n\n');
    navigator.clipboard.writeText(allText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadBill = async (format) => {
    const lastDraft = history[history.length - 1]?.response;
    if (!lastDraft) return;

    // Extract only the actual bill text — find where the bill starts
    const billStartMatch = lastDraft.match(/(HOUSE BILL NO|SENATE BILL NO)/i);
    const billText = billStartMatch
      ? lastDraft.slice(lastDraft.search(/(HOUSE BILL NO|SENATE BILL NO)/i))
      : lastDraft;

    // Extract bill title from first real bill line
    const firstLine = billText.split('\n').find(l => l.trim()) || 'Michigan_Bill';
    const billTitle = firstLine.replace(/[^a-zA-Z0-9 ]/g, '').trim().slice(0, 50);

    try {
      const response = await fetch('https://legisim-server.onrender.com/api/generate-bill', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ billText, format, billTitle }),
      });

      if (!response.ok) {
        const err = await response.json();
        alert(`Error: ${err.error}\n\n${err.detail || ''}`);
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${billTitle.replace(/\s+/g, '_')}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Download failed. Make sure the server is running.');
    }
  };

  const isAnalyzer  = mode === 'analyzer';
  const isLobby     = mode === 'lobby';
  const isTestimony = mode === 'testimony';
  const isAmendment = mode === 'amendment';
  const isFloorVote = mode === 'floatvote';

  return (
    <>
      <style>{CSS}</style>
      <MobileMenuCSS />
      <div className="page">

        {/* MASTHEAD */}
        <div className="legisim-masthead">
          <div className="masthead-left">
            <div className="masthead-wordmark">Whip<em>Count</em><sup>TM</sup></div>
            <button className="mast-back" onClick={() => onNavigate("dashboard")}>← Dashboard</button>
          </div>
          <div className="masthead-right">
            <button className="mast-btn-sm" onClick={() => onNavigate("account")}>Account</button>
            <button className="mast-btn-sm signout" onClick={() => handleSignOut(supabase, onNavigate)}>Sign Out</button>
          </div>
        </div>


        {/* BODY */}
        <div className="body">

          {isFloorVote ? (
            <div style={{display:'contents'}}>
            {/* INPUT PANEL — Floor Vote, same frame as all other modes */}
            <div className="input-panel">
              <div className="toggle-grid">
                <button className={`toggle-btn${isAnalyzer ? ' active analyzer' : ''}`} onClick={() => { setMode('analyzer'); }}>Bill Analyzer</button>
                <button className={`toggle-btn${mode === 'creator' ? ' active creator' : ''}`} onClick={() => { setMode('creator'); }}>Bill Creator</button>
                <button className={`toggle-btn${isLobby ? ' active lobby' : ''}`} onClick={() => { setMode('lobby'); }}>Lobby Strategist</button>
                <button className={`toggle-btn${isTestimony ? ' active testimony' : ''}`} onClick={() => { setMode('testimony'); }}>Testimony Writer</button>
                <button className={`toggle-btn${isAmendment ? ' active amendment' : ''}`} onClick={() => { setMode('amendment'); }}>Amendment Builder</button>
                <button className={`toggle-btn active floatvote`}>Floor Vote</button>
              </div>
              <div className="panel-header">
                <div className="panel-title" style={{display:"flex",alignItems:"center",gap:"0.5rem"}}>
                  <span>Floor Vote Simulator</span>
                  {profile?.role !== "admin" && (
                    <>
                      <span style={{opacity:0.5}}>•</span>
                      <span style={{color:fvUsed >= FV_MONTHLY_LIMIT ? "#e05252" : fvUsed >= 20 ? "#e09a52" : "var(--gold)"}}>
                        {FV_MONTHLY_LIMIT - fvUsed}/{FV_MONTHLY_LIMIT} simulations remaining this month
                      </span>
                    </>
                  )}
                </div>
                <div className="panel-sub">Upload a bill, paste text, or describe a policy concept. The AI predicts how every member of the Michigan Senate or House will vote, with individual rationale for each vote.</div>
              </div>
              {/* Chamber toggle */}
              <div style={{padding:'0.75rem 1.25rem 0.75rem',borderBottom:'1px solid var(--border)'}}>
                <div style={{fontFamily:'var(--font-mono)',fontSize:'0.75rem',fontWeight:600,color:'var(--text)',letterSpacing:'0.08em',textTransform:'uppercase',marginBottom:'0.5rem'}}>Chamber</div>
                <div className="position-toggle">
                  <button className={`pos-btn${fvChamber === 'Senate' ? ' active support' : ''}`} onClick={() => { setFvChamber('Senate'); setFvResult(null); }}>🏛 Senate</button>
                  <button className={`pos-btn${fvChamber === 'House' ? ' active support' : ''}`} onClick={() => { setFvChamber('House'); setFvResult(null); }}>🏛 House</button>
                </div>
                <div style={{fontFamily:'var(--font-mono)',fontSize:'0.8rem',color:'var(--muted)',marginTop:'0.5rem',letterSpacing:'0.03em'}}>
                  {fvChamber === 'Senate'
                    ? <><span style={{color:'#5b8fee',fontWeight:700}}>{SENATE_MEMBERS.filter(m=>m.party==='D').length}D</span> / <span style={{color:'#e85555',fontWeight:700}}>{SENATE_MEMBERS.filter(m=>m.party==='R').length}R</span> · D Majority · 19 to pass</>
                    : <><span style={{color:'#5b8fee',fontWeight:700}}>{HOUSE_MEMBERS.filter(m=>m.party==='D').length}D</span> / <span style={{color:'#e85555',fontWeight:700}}>{HOUSE_MEMBERS.filter(m=>m.party==='R').length}R</span> · R Majority · 56 to pass</>
                  }
                </div>
              </div>
              {/* Upload zone */}
              <div style={{padding:'0.75rem 1.25rem 0',flexShrink:0}}>
                <div className={`upload-zone${fvDrag ? ' drag' : ''}`}
                  onClick={() => fvFileRef.current?.click()}
                  onDragOver={e => { e.preventDefault(); setFvDrag(true); }}
                  onDragLeave={() => setFvDrag(false)}
                  onDrop={e => { e.preventDefault(); setFvDrag(false); fvHandleFile(e.dataTransfer.files[0]); }}>
                  <input ref={fvFileRef} type="file" accept=".txt,.pdf,.doc,.docx" style={{display:'none'}} onChange={e => fvHandleFile(e.target.files[0])} />
                  <div className="upload-hint">📎 <strong>Click to upload</strong> or drag · .txt .pdf .doc .docx</div>
                </div>
                {fvFileName && <div style={{fontFamily:'var(--font-mono)',fontSize:'0.65rem',color:'var(--muted)',marginTop:'0.3rem'}}>📄 {fvFileName} <button onClick={() => { setFvFileName(null); setFvBillText(''); setFvPdfWarning(null); }} style={{background:'none',border:'none',color:'var(--muted)',cursor:'pointer',marginLeft:'0.3rem'}}>✕</button></div>}
                {fvPdfWarning && (
                  <div className="pdf-warning" style={{margin:'0.4rem 0 0'}}>{fvPdfWarning}</div>
                )}
              </div>
              {/* Textarea */}
              <textarea
                className="chat-input"
                style={{flex:1,margin:'0.75rem 1.25rem 0',resize:'none',fontFamily:'var(--font-mono)',fontSize:'0.78rem',lineHeight:1.6,minHeight:'80px',maxHeight:'none',height:'auto'}}
                placeholder={"Enter bill to simulate...\n\nExamples:\n• HB 4482 — concealed pistol license reciprocity\n• Paste full bill text\n• Describe any policy concept\n\nCtrl+Enter to simulate."}
                value={fvBillText}
                onChange={e => setFvBillText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) fvSimulate(); }}
              />
              <div style={{padding:'0.65rem 1.25rem 0.9rem',flexShrink:0,display:'flex',gap:'0.6rem',marginTop:'auto'}}>
                <button className="submit-btn floatvote" style={{flex:1,border:'none',cursor:'pointer'}}
                  onClick={fvSimulate} disabled={!fvBillText.trim() || fvLoading || (profile?.role !== "admin" && fvUsed >= FV_MONTHLY_LIMIT)}>
                  {fvLoading ? 'Simulating…' : profile?.role !== "admin" && fvUsed >= FV_MONTHLY_LIMIT ? 'Monthly Limit Reached' : `Simulate ${fvChamber} Vote →`}
                </button>
                {fvResult && <button onClick={() => { setFvResult(null); setFvBillText(''); setFvFileName(null); setFvPdfWarning(null); }} style={{background:'transparent',border:'1px solid var(--border)',borderRadius:'8px',padding:'0.7rem 0.9rem',color:'var(--muted)',fontFamily:'var(--font-mono)',fontSize:'0.75rem',cursor:'pointer'}}>Clear</button>}
              </div>

            </div>
            {/* OUTPUT — pass result into FloorVoteSimulator */}
            <div style={{flex:1,overflow:'hidden',display:'flex',flexDirection:'column',minWidth:0}}>
              <FloorVoteSimulator onNavigate={onNavigate} embedded={true} externalResult={fvResult} externalLoading={fvLoading} externalChamber={fvChamber} />
            </div>
            </div>
          ) : (

          /* NORMAL SPLIT PANEL */
          <>

          {/* INPUT PANEL */}
          <div className="input-panel">
            <div className="toggle-grid">
              <button className={`toggle-btn${isAnalyzer ? ' active analyzer' : ''}`} onClick={() => { setMode('analyzer'); }}>Bill Analyzer</button>
              <button className={`toggle-btn${mode === 'creator' ? ' active creator' : ''}`} onClick={() => { setMode('creator'); }}>Bill Creator</button>
              <button className={`toggle-btn${isLobby ? ' active lobby' : ''}`} onClick={() => { setMode('lobby'); }}>Lobby Strategist</button>
              <button className={`toggle-btn${isTestimony ? ' active testimony' : ''}`} onClick={() => { setMode('testimony'); }}>Testimony Writer</button>
              <button className={`toggle-btn${isAmendment ? ' active amendment' : ''}`} onClick={() => { setMode('amendment'); }}>Amendment Builder</button>
              <button className={`toggle-btn${isFloorVote ? ' active floatvote' : ''}`} onClick={() => { setMode('floatvote'); }}>Floor Vote</button>
            </div>
            <div className="panel-header">
              <div className="panel-title">
                {isAnalyzer ? 'Bill Analyzer' : isLobby ? 'Lobby Strategist' : isTestimony ? 'Testimony Writer' : isAmendment ? 'Amendment Builder' : isFloorVote ? 'Floor Vote Simulator' : 'Bill Creator'}
              </div>
              <div className="panel-sub">
                {isAnalyzer
                  ? 'Upload a bill, paste text, or type a bill number. The AI will produce a full structured analysis — summary, fiscal impact, stakeholder effects, and legislative status.'
                  : isLobby
                  ? 'Enter a bill number or describe your policy goal. The AI generates legislator-by-legislator persuasion arguments, caucus strategy, and vote-by-vote talking points.'
                  : isTestimony
                  ? 'Fill in the fields below and the AI will draft a complete written testimony, an oral script timed to your limit, and a set of talking points — ready to submit or deliver.'
                  : isAmendment
                  ? 'Describe the change you want to make and provide the bill text or number. The AI will draft the amendment in authentic Michigan legislative format with proper redline notation.'
                  : 'Describe a bill concept in plain language. The AI will draft it in authentic Michigan legislative bill format — short title, enacting clause, and fully structured statutory language.'}
              </div>
            </div>

            {isTestimony ? (
              <>
              <div className="testimony-form">
                <div className="form-field">
                  <div className="form-label">Bill Number or Description</div>
                  <div style={{display:'flex',gap:'0.5rem',alignItems:'center'}}>
                    <input
                      className="form-input"
                      placeholder="e.g. HB4001 or SB 99"
                      value={testimonyForm.bill}
                      style={{flex:1}}
                      onChange={e => { setTestimonyForm(f => ({...f, bill: e.target.value})); setTestimonyBillData(null); setTestimonyBillError(null); }}
                      onKeyDown={e => e.key === 'Enter' && handleTestimonyBillLookup()}
                    />
                    <button
                      onClick={handleTestimonyBillLookup}
                      disabled={testimonyBillLoading || !testimonyForm.bill.trim()}
                      style={{background:'var(--gold-glow)',border:'1px solid var(--gold-dim)',borderRadius:'6px',padding:'0.45rem 0.9rem',color:'var(--gold)',fontFamily:'var(--font-mono)',fontSize:'0.68rem',cursor:'pointer',whiteSpace:'nowrap',letterSpacing:'0.06em'}}
                    >
                      {testimonyBillLoading ? 'Loading…' : 'Find Bill'}
                    </button>
                  </div>
                  {testimonyBillError && <div style={{color:'#f87171',fontFamily:'var(--font-mono)',fontSize:'0.65rem',marginTop:'0.25rem'}}>{testimonyBillError}</div>}
                  {testimonyBillData && (
                    <div style={{fontFamily:'var(--font-mono)',fontSize:'0.65rem',color:'var(--gold)',marginTop:'0.25rem',padding:'0.3rem 0.5rem',background:'rgba(201,168,76,0.06)',border:'1px solid var(--gold-dim)',borderRadius:'4px'}}>
                      ✦ {testimonyBillData.num} — {testimonyBillData.bill.title?.slice(0,70)}…
                      {testimonyBillData.billText && <span style={{color:'var(--muted)',marginLeft:'0.5rem'}}>· Full text loaded</span>}
                    </div>
                  )}
                </div>
                <div className="form-field">
                  <div className="form-label">Your Organization / Name</div>
                  <input className="form-input" placeholder="e.g. Michigan Licensed Beverage Association" value={testimonyForm.organization} onChange={e => setTestimonyForm(f => ({...f, organization: e.target.value}))} />
                </div>
                <div className="form-field">
                  <div className="form-label">Your Title <span style={{fontWeight:300,opacity:0.6}}>(optional)</span></div>
                  <input className="form-input" placeholder="e.g. Executive Director, Policy Director, CEO" value={testimonyForm.title} onChange={e => setTestimonyForm(f => ({...f, title: e.target.value}))} />
                </div>
                <div className="form-field">
                  <div className="form-label">Position</div>
                  <div className="position-toggle">
                    <button className={`pos-btn${testimonyForm.position === 'support' ? ' active support' : ''}`} onClick={() => setTestimonyForm(f => ({...f, position: 'support'}))}>✓ Support</button>
                    <button className={`pos-btn${testimonyForm.position === 'oppose' ? ' active oppose' : ''}`} onClick={() => setTestimonyForm(f => ({...f, position: 'oppose'}))}>✗ Oppose</button>
                  </div>
          </div>
        <div className="form-field">
                  <div className="form-label">Committee</div>
                  <div className="position-toggle" style={{marginBottom:"0.4rem"}}>
                    <button className={`pos-btn${testimonyForm.chamber === 'House' ? ' active support' : ''}`} onClick={() => setTestimonyForm(f => ({...f, chamber: 'House'}))}>🏛 House</button>
                    <button className={`pos-btn${testimonyForm.chamber === 'Senate' ? ' active support' : ''}`} onClick={() => setTestimonyForm(f => ({...f, chamber: 'Senate'}))}>🏛 Senate</button>
                  </div>
                  <input className="form-input" placeholder="e.g. Energy Committee" value={testimonyForm.committee} onChange={e => setTestimonyForm(f => ({...f, committee: e.target.value}))} />
                </div>
                <div className="form-field">
                  <div className="form-label">Oral Testimony Time Limit</div>
                  <select className="form-select" value={testimonyForm.timeLimit} onChange={e => setTestimonyForm(f => ({...f, timeLimit: e.target.value}))}>
                    <option value="1">1 minute</option>
                    <option value="2">2 minutes</option>
                    <option value="3">3 minutes</option>
                    <option value="4">4 minutes</option>
                    <option value="5">5 minutes</option>
                  </select>
                </div>
                <div className="form-field" style={{flex:1,display:'flex',flexDirection:'column'}}>
                  <div className="form-label">Additional Context <span style={{fontWeight:300,opacity:0.6}}>(optional)</span></div>
                  <textarea
                    className="form-input"
                    placeholder="Add any specific points, constituent stories, local impacts, or arguments you want the AI to incorporate..."
                    value={testimonyForm.additionalContext}
                    onChange={e => setTestimonyForm(f => ({...f, additionalContext: e.target.value}))}
                    style={{ resize:'none', flex:1, minHeight:'100px', fontFamily:'inherit', lineHeight:1.5 }}
                  />
                </div>
              </div>
              {/* Testimony submit footer — same size as other mode buttons */}
              <div style={{padding:'0.65rem 1.5rem 0.9rem',flexShrink:0,borderTop:'1px solid var(--border)'}}>
                <button
                  className="submit-btn testimony"
                  style={{width:'100%'}}
                  onClick={handleSubmit}
                  disabled={loading || !testimonyForm.bill.trim() || !testimonyForm.organization.trim() || !testimonyForm.committee.trim()}
                >
                  {loading ? 'Generating…' : '🎤 Generate Testimony →'}
                </button>
              </div>
              </>
            ) : isAmendment ? (
              /* ── AMENDMENT BUILDER ─────────────────────────────────── */
              <div className="testimony-form">
                <div className="form-field">
                  <div className="form-label">Find Bill to Amend</div>
                  <div style={{display:'flex',gap:'0.5rem',alignItems:'center'}}>
                    <input
                      className="form-input"
                      placeholder="e.g. HB 5614 or SB 99"
                      value={amendBillNum}
                      onChange={e => setAmendBillNum(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleAmendBillLookup()}
                      style={{flex:1}}
                    />
                    <button
                      onClick={handleAmendBillLookup}
                      disabled={amendBillLoading || !amendBillNum.trim()}
                      style={{background:'var(--gold-glow)',border:'1px solid var(--gold-dim)',borderRadius:'6px',padding:'0.45rem 0.9rem',color:'var(--gold)',fontFamily:'var(--font-mono)',fontSize:'0.68rem',cursor:'pointer',whiteSpace:'nowrap',letterSpacing:'0.06em'}}
                    >
                      {amendBillLoading ? 'Loading…' : 'Find Bill'}
                    </button>
                  </div>
                  {amendBillError && <div style={{color:'#f87171',fontFamily:'var(--font-mono)',fontSize:'0.65rem',marginTop:'0.25rem'}}>{amendBillError}</div>}
                  {amendBillData && (
                    <div style={{fontFamily:'var(--font-mono)',fontSize:'0.65rem',color:'var(--gold)',marginTop:'0.25rem',padding:'0.3rem 0.5rem',background:'rgba(201,168,76,0.06)',border:'1px solid var(--gold-dim)',borderRadius:'4px'}}>
                      ✦ {amendBillData.num} — {amendBillData.bill.title?.slice(0,80)}
                      {amendBillData.billText && <span style={{color:'var(--muted)',marginLeft:'0.5rem'}}>· Full text loaded</span>}
                    </div>
                  )}
                </div>
                {/* Amendment upload zone — always visible */}
                <div className="form-field">
                  <div className="form-label">Upload Bill Text <span style={{fontWeight:300,opacity:0.6}}>(optional)</span></div>
                  <div
                    className={`upload-zone${drag ? ' drag' : ''}`}
                    style={{margin:0}}
                    onClick={() => amendFileRef.current?.click()}
                    onDragOver={e => { e.preventDefault(); setDrag(true); }}
                    onDragLeave={() => setDrag(false)}
                    onDrop={e => { e.preventDefault(); setDrag(false); handleAmendFile(e.dataTransfer.files[0]); }}
                  >
                    <input ref={amendFileRef} type="file" accept=".txt,.pdf,.doc,.docx" style={{display:'none'}} onChange={e => handleAmendFile(e.target.files[0])} />
                    <div className="upload-hint">📎 <strong>Click to upload</strong> or drag · .txt .pdf .doc .docx</div>
                  </div>
                  {amendFileName && (
                    <div className="uploaded-file">
                      <span>📄 {amendFileName}</span>
                      <button onClick={() => { setAmendFileName(null); setAmendPrompt(''); setAmendPdfWarning(null); }}>✕</button>
                    </div>
                  )}
                  {amendPdfWarning && <div className="pdf-warning" style={{margin:'0.4rem 0 0'}}>{amendPdfWarning}</div>}
                </div>

                {amendBillData && (
                  <div className="form-field" style={{flex:1,display:'flex',flexDirection:'column'}}>
                    <div className="form-label">Describe Your Amendment</div>
                    <textarea
                      className="bill-textarea"
                      style={{flex:1,minHeight:'160px'}}
                      placeholder={`Describe the change you want to make...\n\nExamples:\n• "Change the effective date from 2026 to 2027"\n• "Add an exemption for nonprofits under Section 3"\n• "Remove the $500 fine in Section 5(b) and replace with a warning"\n• "Add a new section requiring annual reporting to the legislature"`}
                      value={amendPrompt}
                      onChange={e => setAmendPrompt(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) handleAmendSubmit(); }}
                    />
                    <div className="submit-row">
                      <button
                        className="submit-btn amendment"
                        onClick={handleAmendSubmit}
                        disabled={loading || !amendPrompt.trim()}
                      >
                        {loading ? 'Building Amendment…' : 'Build Amendment →'}
                      </button>
                      {history.length > 0 && (
                        <button className="clear-btn" onClick={() => setHistory([])}>Clear</button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : isFloorVote ? (
              /* ── FLOOR VOTE — full-width embed, hides normal panels ── */
              null
            ) : (
              <>
                {isLobby && (
                  <div className="form-field" style={{padding:'1rem 1.5rem 0',flexShrink:0}}>
                    <div className="form-label">Bill Number <span style={{fontWeight:300,opacity:0.6}}>(optional)</span></div>
                    <div style={{display:'flex',gap:'0.5rem',alignItems:'center'}}>
                      <input
                        className="form-input"
                        style={{flex:1}}
                        placeholder="e.g. HB4183 or SB 99"
                        value={lobbyBillNum}
                        onChange={e => setLobbyBillNum(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleLobbyBillLookup()}
                      />
                      <button
                        onClick={handleLobbyBillLookup}
                        disabled={lobbyBillLoading || !lobbyBillNum.trim()}
                        style={{background:'var(--gold-glow)',border:'1px solid var(--gold-dim)',borderRadius:'6px',padding:'0.45rem 0.9rem',color:'var(--gold)',fontFamily:'var(--font-mono)',fontSize:'0.68rem',cursor:'pointer',whiteSpace:'nowrap',letterSpacing:'0.06em'}}
                      >
                        {lobbyBillLoading ? 'Loading…' : 'Load Bill'}
                      </button>
                    </div>
                    {lobbyBillError && <div style={{color:'#f87171',fontFamily:'var(--font-mono)',fontSize:'0.65rem',marginTop:'0.25rem'}}>{lobbyBillError}</div>}
                    {lobbyBillData && (
                      <div style={{fontFamily:'var(--font-mono)',fontSize:'0.65rem',color:'var(--gold)',marginTop:'0.25rem',padding:'0.3rem 0.5rem',background:'rgba(201,168,76,0.06)',border:'1px solid var(--gold-dim)',borderRadius:'4px'}}>
                        ✦ {lobbyBillData.num} — {lobbyBillData.bill.title?.slice(0,80)}…
                        {lobbyBillData.billText && <span style={{color:'var(--muted)',marginLeft:'0.5rem'}}>· Full text loaded</span>}
                      </div>
                    )}
                  </div>
                )}
                <div
                  className={`upload-zone${drag ? ' drag' : ''}`}
                  onClick={() => fileRef.current?.click()}
                  onDragOver={e => { e.preventDefault(); setDrag(true); }}
                  onDragLeave={() => setDrag(false)}
                  onDrop={handleDrop}
                >
                  <input ref={fileRef} type="file" accept=".txt,.pdf,.doc,.docx" onChange={e => handleFile(e.target.files[0])} />
                  <div className="upload-hint">📎 <strong>Click to upload</strong> or drag · .txt .pdf .doc .docx</div>
                </div>
                {fileName && (
                  <div className="uploaded-file">
                    <span>📄 {fileName}</span>
                    <button onClick={() => { setFileName(null); setBillText(''); setPdfWarning(null); }}>✕</button>
                  </div>
                )}
                {pdfWarning && (
                  <div className="pdf-warning">{pdfWarning}</div>
                )}
                <textarea
                  ref={textareaRef}
                  className="bill-textarea"
                  placeholder={isAnalyzer
                    ? `Paste or type bill text here...\n\nCtrl+Enter to submit.`
                    : `Describe your bill idea here...\n\nCtrl+Enter to submit.`
                  }
                  value={billText}
                  onChange={e => setBillText(e.target.value)}
                  onKeyDown={handleKey}
                />
                <div className="submit-row">
                  <button
                    className={`submit-btn ${mode}`}
                    onClick={handleSubmit}
                    disabled={!billText.trim() || loading}
                  >
                    {loading
                      ? (isAnalyzer ? 'Analyzing…' : isLobby ? 'Building Strategy…' : isAmendment ? 'Building Amendment…' : 'Drafting…')
                      : (isAnalyzer ? 'Analyze Bill →' : isLobby ? 'Build Lobby Strategy →' : 'Draft Bill →')}
                  </button>
                  {history.length > 0 && (
                    <button className="clear-btn" onClick={() => setHistory([])}>Clear</button>
                  )}
                </div>
              </>
            )}
          </div>

          {/* OUTPUT PANEL */}
          <div className="output-panel">
              {/* Loading overlay — covers full output panel regardless of scroll position */}
              {loading && history.length > 0 && (
                <div style={{position:"absolute",inset:0,background:"rgba(13,13,20,0.92)",zIndex:20,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:"1.5rem",backdropFilter:"blur(3px)"}}>
                  <div style={{fontFamily:"var(--font-display)",fontSize:"3.5rem",fontWeight:900,color:"#ef4444",letterSpacing:"0.15em",textTransform:"uppercase"}}>UPDATING</div>
                  <div style={{display:"flex",gap:"10px"}}>
                    <div className="typing-dot" style={{width:14,height:14,background:"#ef4444"}}/>
                    <div className="typing-dot" style={{width:14,height:14,background:"#ef4444",animationDelay:"0.2s"}}/>
                    <div className="typing-dot" style={{width:14,height:14,background:"#ef4444",animationDelay:"0.4s"}}/>
                  </div>
                </div>
              )}
            <div className="output-header">
              <div className="output-label">
                {isAnalyzer ? 'Analysis Output' : isLobby ? 'Lobby Strategy' : isTestimony ? 'Testimony Output' : isAmendment ? 'Amendment Output' : 'Draft Output'}
              </div>
              <div style={{display:"flex", gap:"0.5rem", alignItems:"center"}}>
                {(!isAnalyzer && !isLobby && !isTestimony && !isAmendment) && history.length > 0 && (
                  <>
                    <button className="copy-btn" onClick={() => downloadBill('docx')} style={{borderColor:"var(--blue)", color:"var(--blue)"}}>⬇ Word (.docx)</button>
                    <button className="copy-btn" onClick={() => downloadBill('pdf')} style={{borderColor:"var(--rep)", color:"var(--rep)"}}>⬇ PDF</button>
                  </>
                )}
                {isAmendment && history.length > 0 && (
                  <>
                    <button className="copy-btn" onClick={() => downloadBill('docx')} style={{borderColor:"#0891b2", color:"#0891b2"}}>⬇ Word (.docx)</button>
                    <button className="copy-btn" onClick={() => downloadBill('pdf')} style={{borderColor:"#0891b2", color:"#0891b2"}}>⬇ PDF</button>
                  </>
                )}
                {isLobby && history.length > 0 && (
                  <div className="whip-btn-wrap" ref={whipMenuRef}>
                    <button
                      className="whip-btn"
                      onClick={() => setWhipSheetMenu(m => !m)}
                      disabled={whipSheetLoading}
                    >
                      {whipSheetLoading ? '⏳ Generating…' : '📋 Create WhipSheet ▾'}
                    </button>
                    {whipSheetMenu && (
                      <div className="whip-menu">
                        <button className="whip-menu-item" onClick={() => generateWhipSheet('pdf')}>⬇ PDF</button>
                        <button className="whip-menu-item" onClick={() => generateWhipSheet('docx')}>⬇ Word (.docx)</button>
                        <button className="whip-menu-item" onClick={() => generateWhipSheet('txt')}>⬇ Plain Text (.txt)</button>
                      </div>
                    )}
                  </div>
                )}
                {history.length > 0 && (
                  <button className={`copy-btn${copied ? ' copied' : ''}`} onClick={copyAll}>
                    {copied ? '✓ Copied' : 'Copy Text'}
                  </button>
                )}
              </div>
            </div>
            <div className="output-body" ref={outputRef}>
              {history.length === 0 && !loading ? (
                <div className="empty-output">
                  <div className="empty-glyph">{isAnalyzer ? '📋' : isTestimony ? '🎤' : isAmendment ? '📝' : '✍'}</div>
                  <div className="empty-title">
                    {isAnalyzer ? 'Ready to analyze' : isTestimony ? 'Ready to write testimony' : isAmendment ? 'Ready to build amendment' : 'Ready to draft'}
                  </div>
                  <div className="empty-sub">
                    {isAnalyzer
                      ? 'Paste or upload a bill on the left and click Analyze Bill to receive a thorough breakdown.'
                      : isTestimony
                      ? 'Fill in the form on the left and click Generate Testimony to receive written testimony, an oral script, and talking points.'
                      : 'Describe your bill concept on the left and click Draft Bill to receive a complete Michigan-formatted legislative draft.'}
                  </div>
                </div>
              ) : (
                <div className="output-content">
                  {history.map((turn, i) => (
                    <div key={i} className="history-turn">
                      <div className="history-query">
                        <span>{isAnalyzer ? 'Query' : isTestimony ? 'Request' : 'Concept'} {i + 1}</span> — {turn.query.length > 80 ? turn.query.slice(0, 80) + '…' : turn.query}
                      </div>
                      {isAnalyzer
                        ? renderOutput(turn.response)
                        : <div className="bill-draft">{turn.response}</div>
                      }
                    </div>
                  ))}
                  {loading && (
                    <div className="typing">
                      <div className="typing-dot" />
                      <div className="typing-dot" />
                      <div className="typing-dot" />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* FOLLOW-UP CHAT BAR — visible once there's output to revise */}
            {history.length > 0 && (
              <div className="chat-bar">
                <textarea
                  ref={followUpRef}
                  className="chat-input"
                  placeholder={
                    mode === 'analyzer' ? "Ask a follow-up question about this bill…"
                    : mode === 'testimony' ? "Revise the testimony — e.g. 'make it more aggressive' or 'add a section on fiscal impact'…"
                    : mode === 'lobby' ? "Refine the strategy — e.g. 'focus on Senate Republicans' or 'add talking points for appropriations committee'…"
                    : mode === 'amendment' ? "Refine the amendment — e.g. 'also change the penalty in Section 7' or 'add a 90-day implementation period'…"
                    : "Revise the draft — e.g. 'add an enforcement provision' or 'change the effective date to 2027'…"
                  }
                  value={followUp}
                  onChange={e => setFollowUp(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleFollowUp(); } }}
                  rows={1}
                />
                <button
                  className="chat-send"
                  onClick={handleFollowUp}
                  disabled={!followUp.trim() || loading}
                >
                  {loading ? '…' : 'Send ↑'}
                </button>
              </div>
            )}
          </div>

          </> 
          )}

        </div>
      </div>
    </>
  );
}
